<template>
  <div class="revenue-statistics-page">
    <Watermark />

    <!-- 顶部校区选择 & PC 端日期筛选：PC 为表单式下拉，移动端为底部弹层 -->
    <div :class="isPC ? 'header-section-pc' : 'header-section'">
      <template v-if="isPC">
        <div class="header-row-pc">
          <div class="org-select-row-pc">
            <div ref="orgSelectWrapRef" class="org-select-wrap">
              <div
                class="org-select-trigger"
                :class="{ 'is-open': showOrgPcDropdown }"
                role="button"
                tabindex="0"
                @click.stop="toggleOrgPcDropdown"
                @keydown.enter.prevent="toggleOrgPcDropdown"
                @keydown.space.prevent="toggleOrgPcDropdown"
              >
                <span class="org-select-trigger-text">{{ selectedOrgName }}</span>
                <van-icon class="org-select-chevron" :name="showOrgPcDropdown ? 'arrow-up' : 'arrow-down'" />
              </div>
              <div
                v-show="showOrgPcDropdown"
                class="org-select-dropdown"
                @click.stop
              >
                <ul class="org-select-list">
                  <li
                    v-for="opt in orgOptions"
                    :key="String(opt.value)"
                    class="org-select-item"
                    :class="{ active: String(opt.value) === String(selectedOrg) }"
                    @click="selectOrgPc(opt)"
                  >
                    {{ opt.text }}
                  </li>
                </ul>
              </div>
            </div>
          </div>
          <div class="pc-date-filter-bar-inline">
            <div class="pc-date-quick-tabs">
              <button
                v-for="key in PC_DATE_QUICK_KEYS"
                :key="key"
                type="button"
                class="pc-date-tab"
                :class="{ active: currentQuickType === key }"
                @click="handlePcQuickDate(key)"
              >
                {{ QUICK_TEXT[key] }}
              </button>
            </div>
            <div
              ref="pcDateRangeInputRef"
              class="pc-date-range-input"
              :class="{ 'is-custom': currentQuickType === QUICK_KEY.custom }"
              @click.stop="openPcRangePicker"
            >
              <van-icon name="calendar-o" class="pc-date-range-input__icon" />
              <div class="pc-date-range-input__inner">
                <span
                  class="pc-date-range-input__part"
                  :class="{ 'is-ph': !pcDisplayRange[0] }"
                >{{ pcDisplayRange[0] || '开始日期' }}</span>
                <span class="pc-date-range-input__sep">至</span>
                <span
                  class="pc-date-range-input__part"
                  :class="{ 'is-ph': !pcDisplayRange[1] }"
                >{{ pcDisplayRange[1] || '结束日期' }}</span>
              </div>
            </div>
            <van-button
              size="small"
              type="primary"
              :loading="refreshing"
              class="refresh-btn-pc"
              @click="handleRefresh"
            >
              <template #icon>
                <van-icon name="replay" :class="{ 'is-spinning': refreshing }" />
              </template>
              刷新
            </van-button>
          </div>
        </div>
      </template>
      <template v-else>
        <div class="h5-header-row">
          <div class="org-select-card">
            <div class="org-select-btn" @click="showOrgPicker = true">
              {{ selectedOrgName }} <van-icon name="arrow-down" />
            </div>
          </div>
          <div class="h5-header-right">
            <van-button
              size="small"
              type="default"
              :loading="refreshing"
              class="refresh-btn-h5"
              @click="handleRefresh"
            >
              <template #icon>
                <van-icon name="replay" :class="{ 'is-spinning': refreshing }" />
              </template>
            </van-button>
            <van-button
              size="small"
              type="default"
              @click="showDateFilter = true"
              class="month-btn"
            >
              {{ currentDateRangeDisplay }} <van-icon name="arrow-down" />
            </van-button>
          </div>
        </div>
      </template>
    </div>

    <!-- 校区选择底部弹层（仅移动端） -->
    <van-action-sheet
      v-if="!isPC"
      v-model:show="showOrgPicker"
      :actions="orgOptions.map(item => ({ name: item.text, value: item.value }))"
      cancel-text="取消"
      @select="handleOrgSelect"
    />

    <!-- 当月统计概览 -->
    <div class="overview-section">
      
        <div class="section-title">
          <span class="title-icon"></span>
          当月统计
        
      </div>
      <div class="overview-cards">
        <div class="overview-card income">
          <div class="card-content">
            <div class="card-value">{{ formatMoney(monthOverview.currentIncome) }}</div>
            <div class="card-label">当月实收(元)</div>
          </div>
        </div>
        <div class="overview-card refund">
          <div class="card-content">
            <div class="card-value">{{ formatMoney(monthOverview.currentRefund) }}</div>
            <div class="card-label">当月实退(元)</div>
          </div>
        </div>
      </div>
    </div>

    <!-- 时间范围内统计 -->
    <div class="stats-section">
      <div class="overview-header">
      <div class="section-title">
        <span class="title-icon period-icon"></span>
        营收统计
      </div>
    </div>
      <div class="stats-cards">
        <div class="stat-card">
          <div class="stat-value income-color">{{ formatMoney(periodStats.actualIncome) }}</div>
          <div class="stat-label">实收金额</div>
        </div>
        <div class="stat-card">
          <div class="stat-value refund-color">{{ formatMoney(periodStats.actualRefund) }}</div>
          <div class="stat-label">实退金额</div>
        </div>
        <div class="stat-card">
          <div class="stat-value">{{ periodStats.signUpCount || 0 }}</div>
          <div class="stat-label">报名人数</div>
        </div>
        <div class="stat-card">
          <div class="stat-value">{{ periodStats.refundCount || 0 }}</div>
          <div class="stat-label">退费人数</div>
        </div>
      </div>
    </div>

    

    <!-- 实时班课在班人数统计 -->
    <div class="chart-section">
      <div class="section-title">
        <span class="title-icon class-student-icon"></span>
        实时班课在班统计
      </div>
      <div class="chart-legend" v-if="!isAllOrg">
        <span v-for="(item, index) in classStudentLegend" :key="index" class="legend-item">
          <span class="legend-dot" :style="{ background: classStudentColors[index] }"></span>
          {{ item }}
        </span>
      </div>
      <div class="chart-dim-toggle chart-dim-toggle--three">
        <span
          v-for="opt in classStudentChartDimensionOptions"
          :key="opt.value"
          class="toggle-btn"
          :class="{ active: classStudentChartDimension === opt.value }"
          @click="handleClassStudentChartDimensionChange(opt.value)"
        >
          {{ opt.label }}
        </span>
      </div>
      <div class="bar-chart-wrapper">
        <div ref="classStudentChart" class="bar-chart"></div>
        <van-empty v-if="classStudentChartData.length === 0" description="暂无数据" />
      </div>
    </div>

    <!-- 分校区统计柱状图 -->
    <div class="chart-section">
      <div class="section-title">
        <span class="title-icon branch-icon"></span>
        实收分校区统计
      </div>
      <div class="chart-legend" v-if="!isAllOrg">
        <span v-for="(item, index) in branchLegend" :key="index" class="legend-item">
          <span class="legend-dot" :style="{ background: branchColors[index] }"></span>
          {{ item }}
        </span>
      </div>
      <div class="chart-dim-toggle">
        <span
          v-for="opt in chartDimensionOptions"
          :key="opt.value"
          class="toggle-btn"
          :class="{ active: branchChartDimension === opt.value }"
          @click="handleBranchChartDimensionChange(opt.value)"
        >
          {{ opt.label }}
        </span>
      </div>
      <div class="bar-chart-wrapper">
        <div ref="branchChart" class="bar-chart"></div>
        <van-empty v-if="branchChartData.length === 0" description="暂无数据" />
      </div>
    </div>

    <!-- 实收分类统计 -->
    <div class="chart-section">
      <div class="section-title">
        <span class="title-icon signup-icon"></span>
        实收分类统计
      </div>
      <div class="chart-legend" v-if="!isAllOrg">
        <span v-for="(item, index) in signUpCategoryLegend" :key="index" class="legend-item">
          <span class="legend-dot" :style="{ background: signUpCategoryColors[index] }"></span>
          {{ item }}
        </span>
      </div>
      <div class="chart-dim-toggle">
        <span
          v-for="opt in chartDimensionOptions"
          :key="opt.value"
          class="toggle-btn"
          :class="{ active: signUpCategoryChartDimension === opt.value }"
          @click="handleSignUpCategoryChartDimensionChange(opt.value)"
        >
          {{ opt.label }}
        </span>
      </div>
      <div class="bar-chart-wrapper">
        <div ref="signUpCategoryChart" class="bar-chart"></div>
        <van-empty v-if="signUpCategoryChartData.length === 0" description="暂无数据" />
      </div>
    </div>

    <!-- 报课金额学部统计堆叠图 -->
    <div class="chart-section">
      <div class="section-title">
        <span class="title-icon subject-icon"></span>
        报课学部统计
      </div>
      <div class="chart-legend" v-if="!isAllOrg">
        <span v-for="(item, index) in subjectLegend" :key="index" class="legend-item">
          <span class="legend-dot" :style="{ background: subjectColors[index] }"></span>
          {{ item }}
        </span>
      </div>
      <div class="chart-dim-toggle">
        <span
          v-for="opt in chartDimensionOptions"
          :key="opt.value"
          class="toggle-btn"
          :class="{ active: subjectChartDimension === opt.value }"
          @click="handleSubjectChartDimensionChange(opt.value)"
        >
          {{ opt.label }}
        </span>
      </div>
      <div class="bar-chart-wrapper">
        <div ref="subjectChart" class="bar-chart"></div>
        <van-empty v-if="subjectChartData.length === 0" description="暂无数据" />
      </div>
    </div>

    <!-- 报课金额分类统计（班课/一对一） -->
    <div class="chart-section">
      <div class="section-title">
        <span class="title-icon course-icon"></span>
        报课分类统计
      </div>
      <div class="chart-legend" v-if="!isAllOrg">
        <span v-for="(item, index) in courseTypeLegend" :key="index" class="legend-item">
          <span class="legend-dot" :style="{ background: courseTypeColors[index] }"></span>
          {{ item }}
        </span>
      </div>
      <div class="chart-dim-toggle">
        <span
          v-for="opt in chartDimensionOptions"
          :key="opt.value"
          class="toggle-btn"
          :class="{ active: courseTypeChartDimension === opt.value }"
          @click="handleCourseTypeChartDimensionChange(opt.value)"
        >
          {{ opt.label }}
        </span>
      </div>
      <div class="bar-chart-wrapper">
        <div ref="courseTypeChart" class="bar-chart"></div>
        <van-empty v-if="courseTypeChartData.length === 0" description="暂无数据" />
      </div>
    </div>

    <!-- 报课金额季节分类统计 -->
    <div class="chart-section">
      <div class="section-title">
        <span class="title-icon season-icon"></span>
        报课季节分类统计
      </div>
      <div class="chart-legend" v-if="!isAllOrg">
        <span v-for="(item, index) in seasonLegend" :key="index" class="legend-item">
          <span class="legend-dot" :style="{ background: seasonColors[index] }"></span>
          {{ item }}
        </span>
      </div>
      <div class="chart-dim-toggle">
        <span
          v-for="opt in chartDimensionOptions"
          :key="opt.value"
          class="toggle-btn"
          :class="{ active: seasonChartDimension === opt.value }"
          @click="handleSeasonChartDimensionChange(opt.value)"
        >
          {{ opt.label }}
        </span>
      </div>
      <div class="bar-chart-wrapper">
        <div ref="seasonChart" class="bar-chart"></div>
        <van-empty v-if="seasonChartData.length === 0" description="暂无数据" />
      </div>
    </div>

     <!-- 报课分校区统计 -->
     <div class="chart-section">
      <div class="section-title">
        <span class="title-icon enroll-org-icon"></span>
        报课分校区统计
      </div>
      <div class="chart-legend" v-if="!isAllOrg">
        <span v-for="(item, index) in enrollOrgLegend" :key="index" class="legend-item">
          <span class="legend-dot" :style="{ background: enrollOrgColors[index] }"></span>
          {{ item }}
        </span>
      </div>
      <div class="bar-chart-wrapper">
        <div ref="enrollOrgChart" class="bar-chart"></div>
        <van-empty v-if="enrollOrgChartData.length === 0" description="暂无数据" />
      </div>
    </div>

    <!-- 报课剩余课时统计 -->
    <div class="chart-section">
      <div class="section-title">
        <span class="title-icon remaining-hours-icon"></span>
        报课剩余课时统计
      </div>
      <div class="chart-dim-toggle">
        <span
          v-for="opt in remainingHoursChartDimensionOptions"
          :key="opt.value"
          class="toggle-btn"
          :class="{ active: remainingHoursChartDimension === opt.value }"
          @click="handleRemainingHoursChartDimensionChange(opt.value)"
        >
          {{ opt.label }}
        </span>
      </div>
      <div class="bar-chart-wrapper">
        <div ref="remainingHoursChart" class="bar-chart"></div>
        <van-empty v-if="remainingHoursChartData.length === 0" description="暂无数据" />
      </div>
    </div>
    

    

    <!-- 空数据提示 -->
    <!-- <EmptyData v-if="!loading && !hasAnyData" tips="暂无营收数据" /> -->

    <!-- 日期筛选面板（移动端） -->
    <van-popup
      v-if="!isPC"
      v-model:show="showDateFilter"
      closeable
      position="bottom"
      round
    >
      <DateFilterPanel
        :config="{ quickType: currentQuickType }"
        @submit="handleDateFilterSubmit"
        @reset="handleDateFilterReset"
      />
    </van-popup>

    <!-- PC 端日期范围选择器 -->
    <PcDateRangePicker
      v-model:show="showPcRangePicker"
      :anchor-rect="pcRangeAnchorRect"
      :start="pcPickerStartStr"
      :end="pcPickerEndStr"
      :min-date="pcCalendarMinDate"
      :max-date="pcCalendarMaxDate"
      @confirm="onPcRangeConfirm"
    />
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, nextTick, onBeforeUnmount, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import * as echarts from 'echarts'
import DateFilterPanel from '@/components/dateFilterPanel/DateFilterPanel.vue'
import PcDateRangePicker from '@/components/dateFilterPanel/PcDateRangePicker.vue'
import EmptyData from '@/components/EmptyData.vue'
import { QUICK_KEY, QUICK_TEXT, getDateRangeByType, formatString } from '@/components/dateFilterPanel/utils.js'
import { postDataPermissionsGetOrgs } from '@/api/report'
import { getRevenueStatistics, getOrgAmountStatistics, getClassStudentCountStatistics, getOrderTypeAmountStatistics, getPeriodAmountStatistics, getCourseTypeAmountStatistics, getSemesterAmountStatistics, getRemainingHoursStatistics, getOrderOrgStatistics } from '@/api/revenue'
import dayjs from 'dayjs'
import { querySyUserGetUserInfo } from '@/api/user'
import Watermark from '@/components/Watermark.vue'

// PC端判断：屏幕宽度 >= 768px
const PC_CHART_MIN_WIDTH = 768
const isPC = ref(
  typeof window !== 'undefined' && window.innerWidth >= PC_CHART_MIN_WIDTH
)

// PC端日期快捷键
const PC_DATE_QUICK_KEYS = [
  QUICK_KEY.today,
  QUICK_KEY.currentWeek,
  QUICK_KEY.currentMonth,
  QUICK_KEY.currentYear,
  QUICK_KEY.prevWeek,
  QUICK_KEY.prevMonth,
  QUICK_KEY.fiscalYear,
]

// PC端日期选择相关
const pcDateRangeInputRef = ref(null)
const showPcRangePicker = ref(false)
const pcRangeAnchorRect = ref(null)

// 校区选择
const selectedOrg = ref('all')
const orgOptions = ref([])
const showOrgPicker = ref(false)
const orgSelectWrapRef = ref(null)
const showOrgPcDropdown = ref(false)

// sessionStorage key
const STORAGE_KEY_ORG = 'revenue_statistics_org'
const STORAGE_KEY_QUICK_TYPE = 'revenue_statistics_quick_type'
const STORAGE_KEY_DATE_RANGE = 'revenue_statistics_date_range'

// 从 sessionStorage 恢复校区选择（需要在 orgOptions 加载完成后调用）
const restoreOrgFromStorage = () => {
  const cached = sessionStorage.getItem(STORAGE_KEY_ORG)
  if (cached && orgOptions.value.length > 0) {
    const exists = orgOptions.value.some(item => item.value === cached)
    if (exists) {
      selectedOrg.value = cached
    }
  }
}

const getUserInfo = async () => {
  try {
    
    const result = await querySyUserGetUserInfo({id: route?.query?.userId})

    if(result.code == '00') {
      localStorage.setItem('userDetail', JSON.stringify(result?.data));
    } 
    
  } catch (error) {
    
  }
}

const selectedOrgName = computed(() => {
  const option = orgOptions.value.find(item => item.value == selectedOrg.value)
  return option?.text || '所有校区'
})

const handleOrgSelect = ({ value }) => {
  selectedOrg.value = value
  sessionStorage.setItem(STORAGE_KEY_ORG, value)
  showOrgPicker.value = false
  resetPageData()
  loadData()
}

// PC端校区选择相关
const toggleOrgPcDropdown = () => {
  showOrgPcDropdown.value = !showOrgPcDropdown.value
}

const selectOrgPc = (opt) => {
  selectedOrg.value = opt.value
  sessionStorage.setItem(STORAGE_KEY_ORG, opt.value)
  showOrgPcDropdown.value = false
  resetPageData()
  loadData()
}

const onDocumentClickCloseOrgPc = (e) => {
  if (!isPC.value || !showOrgPcDropdown.value) return
  const el = orgSelectWrapRef.value
  if (el && !el.contains(e.target)) {
    showOrgPcDropdown.value = false
  }
}

// 窗口大小变化时更新PC端状态
const handleResize = () => {
  const prevPc = isPC.value
  isPC.value = typeof window !== 'undefined' && window.innerWidth >= PC_CHART_MIN_WIDTH
  if (prevPc !== isPC.value) {
    showOrgPcDropdown.value = false
    showOrgPicker.value = false
    showPcRangePicker.value = false
  }
  // 重置图表大小
  branchChartInstance?.resize()
  subjectChartInstance?.resize()
  courseTypeChartInstance?.resize()
  seasonChartInstance?.resize()
  signUpCategoryChartInstance?.resize()
  classStudentChartInstance?.resize()
  enrollOrgChartInstance?.resize()
  remainingHoursChartInstance?.resize()
}

// 日期筛选
const showDateFilter = ref(false)
const currentQuickType = ref(QUICK_KEY.today)
const currentDateRange = ref(getDateRangeByType(QUICK_KEY.today))
const route = useRoute();

const handleDateFilterSubmit = ({ quickType, dateRange }) => {
  currentQuickType.value = quickType
  currentDateRange.value = dateRange
  sessionStorage.setItem(STORAGE_KEY_QUICK_TYPE, quickType)
  sessionStorage.setItem(STORAGE_KEY_DATE_RANGE, JSON.stringify(dateRange))
  showDateFilter.value = false
  // resetChartCache()
  loadData()
}

const handleDateFilterReset = ({ quickType, dateRange }) => {
  currentQuickType.value = quickType
  currentDateRange.value = dateRange
  sessionStorage.setItem(STORAGE_KEY_QUICK_TYPE, quickType)
  sessionStorage.setItem(STORAGE_KEY_DATE_RANGE, JSON.stringify(dateRange))
  showDateFilter.value = false
  // resetChartCache()
  loadData()
}

// PC端快捷日期选择
const handlePcQuickDate = (key) => {
  currentQuickType.value = key
  currentDateRange.value = getDateRangeByType(key)
  sessionStorage.setItem(STORAGE_KEY_QUICK_TYPE, key)
  sessionStorage.setItem(STORAGE_KEY_DATE_RANGE, JSON.stringify(currentDateRange.value))
  resetPageData()
  loadData()
}

// 监听PC端日期选择器开关状态
watch(showPcRangePicker, (open) => {
  if (pcAnchorScrollCleanup) {
    pcAnchorScrollCleanup()
    pcAnchorScrollCleanup = null
  }
  if (!open) return
  readPcRangeAnchorFromDom()
  const onScrollOrResize = () => readPcRangeAnchorFromDom()
  window.addEventListener('scroll', onScrollOrResize, true)
  window.addEventListener('resize', onScrollOrResize)
  pcAnchorScrollCleanup = () => {
    window.removeEventListener('scroll', onScrollOrResize, true)
    window.removeEventListener('resize', onScrollOrResize)
  }
})

// 初始化日期范围 - 先检查缓存
const initDateRange = () => {
  const cachedQuickType = sessionStorage.getItem(STORAGE_KEY_QUICK_TYPE)
  const cachedDateRange = sessionStorage.getItem(STORAGE_KEY_DATE_RANGE)
  if (cachedQuickType) {
    currentQuickType.value = cachedQuickType
  }
  if (cachedDateRange) {
    try {
      currentDateRange.value = JSON.parse(cachedDateRange)
    } catch (e) {
      currentDateRange.value = cachedDateRange.split(',')
    }
  } else {
    // 使用当前的 quickType 来获取对应日期范围，保持一致性
    currentDateRange.value = getDateRangeByType(currentQuickType.value)
  }
}

// 日期筛选按钮展示文案
const currentDateRangeDisplay = computed(() => {
  const type = currentQuickType.value
  const range = currentDateRange.value
  if (type === QUICK_KEY.custom && Array.isArray(range) && range.length >= 2) {
    return `${range[0]} ~ ${range[1]}`
  }
  return QUICK_TEXT[type] || '选择时间'
})

// PC端日期显示范围
const pcDisplayRange = computed(() => {
  const r = currentDateRange.value
  if (Array.isArray(r) && r.length >= 2) return [r[0] || '', r[1] || '']
  return ['', '']
})

const pcPickerStartStr = computed(() => {
  const r = currentDateRange.value
  if (Array.isArray(r) && r[0]) return r[0]
  return dayjs().format(formatString)
})

const pcPickerEndStr = computed(() => {
  const r = currentDateRange.value
  if (Array.isArray(r) && r[1]) return r[1]
  return pcPickerStartStr.value
})

const pcCalendarMinDate = computed(() => dayjs().subtract(2, 'year').toDate())
const pcCalendarMaxDate = computed(() => dayjs().add(1, 'year').toDate())

function readPcRangeAnchorFromDom() {
  const el = pcDateRangeInputRef.value
  if (!el?.getBoundingClientRect) {
    pcRangeAnchorRect.value = null
    return
  }
  const r = el.getBoundingClientRect()
  pcRangeAnchorRect.value = {
    top: r.top,
    left: r.left,
    bottom: r.bottom,
    right: r.right,
    width: r.width,
    height: r.height,
  }
}

let pcAnchorScrollCleanup = null

const openPcRangePicker = () => {
  readPcRangeAnchorFromDom()
  showPcRangePicker.value = true
}

const onPcRangeConfirm = (range) => {
  currentQuickType.value = QUICK_KEY.custom
  currentDateRange.value = range
  sessionStorage.setItem(STORAGE_KEY_QUICK_TYPE, QUICK_KEY.custom)
  sessionStorage.setItem(STORAGE_KEY_DATE_RANGE, JSON.stringify(range))
  resetPageData()
  loadData()
}

// 加载校区列表
const router = useRouter()

const loadOrgList = async () => {
  try {
    const res = await postDataPermissionsGetOrgs()
    if (res.code === '00' && res.data && res.data.length > 0) {
      const options = res.data.map(item => ({
        text: item.name || item.orgName,
        value: item.id
      }))
      orgOptions.value = [
        { text: '所有校区', value: 'all' },
        ...options
      ]
    }
  } catch (error) {
    console.error('加载校区列表失败:', error)
    orgOptions.value = [
      { text: '所有校区', value: 'all' },
      { text: '铁一旗舰校（高中部）', value: '1' },
      { text: '工大旗舰校2023', value: '2' },
      { text: '成都市青羊分校', value: '3' }
    ]
  }
}

// 当月统计概览
const monthOverview = ref({
  currentIncome: 0,
  currentRefund: 0
})

// 时间范围内统计
const periodStats = ref({
  actualIncome: 0,
  actualRefund: 0,
  signUpCount: 0,
  refundCount: 0
})

// 图表数据
const branchChartData = ref([])
const subjectChartData = ref([])
const courseTypeChartData = ref([])
const seasonChartData = ref([])
const signUpCategoryChartData = ref([])
const classStudentChartData = ref([])
const enrollOrgChartData = ref([])
const remainingHoursChartData = ref([])

// 按维度缓存接口原始数据，切换维度时无需重新请求
const rawChartData = ref({
  branch: {},
  subject: {},
  courseType: {},
  season: {},
  signUpCategory: {},
  classStudent: {},
  remainingHours: {}
})
const hasLoadedCharts = ref(false)

// 切换校区/日期时，清空页面所有数据并重置图表，再重新拉取
const resetPageData = () => {
  monthOverview.value = { currentIncome: 0, currentRefund: 0 }
  periodStats.value = { actualIncome: 0, actualRefund: 0, signUpCount: 0, refundCount: 0 }

  branchChartData.value = []
  subjectChartData.value = []
  courseTypeChartData.value = []
  seasonChartData.value = []
  signUpCategoryChartData.value = []
  classStudentChartData.value = []
  enrollOrgChartData.value = []
  remainingHoursChartData.value = []

  rawChartData.value = {
    branch: {},
    subject: {},
    courseType: {},
    season: {},
    signUpCategory: {},
    classStudent: {},
    remainingHours: {}
  }
  hasLoadedCharts.value = false

  ;[
    branchChartInstance,
    subjectChartInstance,
    courseTypeChartInstance,
    seasonChartInstance,
    signUpCategoryChartInstance,
    classStudentChartInstance,
    enrollOrgChartInstance,
    remainingHoursChartInstance
  ].forEach((instance) => {
    instance?.clear?.()
  })
}

// 维度切换且命中缓存后，仅重渲染当前图表（不影响其他图表实例）
const renderCurrentChart = (type) => {
  nextTick(() => {
    switch (type) {
      case 'branch': renderBranchChart(); break
      case 'subject': renderSubjectChart(); break
      case 'courseType': renderCourseTypeChart(); break
      case 'season': renderSeasonChart(); break
      case 'signUpCategory': renderSignUpCategoryChart(); break
      case 'classStudent': renderClassStudentChart(); break
      case 'remainingHours': renderRemainingHoursChart(); break
    }
  })
}

const rawCacheKeyForDimension = (dim) => (people) => (people ? 'people' : 'amount')
const applyBranchData = (raw, dim) => {
  const people = dim === CHART_DIM_PEOPLE
  const orgList =
    people && Array.isArray(raw) && raw.length
      ? raw
      : raw || []
  const bk = {
    income: people ? '实收人数' : '实收金额',
    refund: people ? '退费人数' : '实退金额'
  }
  branchLegendSelected.value = { [bk.income]: true, [bk.refund]: true }
  branchChartData.value = orgList.map(item => ({
    orgId: item.orgId,
    orgName: item.orgName,
    income: people ? numChart(item.enrollmentCount) : numChart(item.receivedAmount),
    refund: people ? numChart(item.refundStudentCount) : numChart(item.refundAmount)
  }))
}

const applySubjectData = (raw, dim) => {
  const people = dim === CHART_DIM_PEOPLE
  subjectChartData.value = (raw || []).map(item => {
    const periodMap = {}
    ;(item.periodDetails || []).forEach(pd => {
      periodMap[pd.period] = people ? numChart(pd.enrollmentCount) : numChart(pd.amount)
    })
    return {
      orgId: item.orgId,
      orgName: item.orgName,
      primaryAmount: periodMap['1'] ?? 0,
      juniorAmount: periodMap['2'] ?? 0,
      seniorAmount: periodMap['3'] ?? 0
    }
  })
}

const applyCourseTypeData = (raw, dim) => {
  const people = dim === CHART_DIM_PEOPLE
  courseTypeChartData.value = (raw || []).map(item => {
    const typeMap = {}
    ;(item.courseTypeDetails || []).forEach(ctd => {
      typeMap[ctd.courseType] = people ? numChart(ctd.enrollmentCount) : numChart(ctd.amount)
    })
    return {
      orgId: item.orgId,
      orgName: item.orgName,
      classAmount: typeMap['1'] ?? 0,
      oneToOneAmount: typeMap['2'] ?? 0
    }
  })
}

const applySeasonData = (raw, dim) => {
  const people = dim === CHART_DIM_PEOPLE
  seasonChartData.value = (raw || []).map(item => {
    const semMap = {}
    ;(item.semesterTypeDetails || []).forEach(std => {
      semMap[std.semesterType] = people ? numChart(std.enrollmentCount) : numChart(std.amount)
    })
    return {
      orgId: item.orgId,
      orgName: item.orgName,
      summerAmount: semMap[1] ?? 0,
      winterAmount: semMap[2] ?? 0,
      autumnAmount: semMap[3] ?? 0,
      springAmount: semMap[4] ?? 0
    }
  })
}

const applySignUpCategoryData = (raw, dim) => {
  const people = dim === CHART_DIM_PEOPLE
  signUpCategoryChartData.value = (raw || []).map(item => {
    const otMap = {}
    ;(item.orderTypeDetails || []).forEach(otd => {
      otMap[otd.orderTag] = people ? numChart(otd.enrollmentCount) : numChart(otd.amount)
    })
    return {
      orgId: item.orgId,
      orgName: item.orgName,
      newSignCount: otMap['1'] ?? 0,
      renewCount: otMap['3'] ?? 0,
      expandCount: otMap['2'] ?? 0
    }
  })
}

const applyClassStudentData = (raw, dim) => {
  const people = dim == CHART_DIM_PEOPLE
  const classDim = dim == CHART_DIM_CLASS
  classStudentChartData.value = (raw || []).map(item => {
    const periodMap = {}
    ;(item.periodDetails || []).forEach(pd => {
      const v = people
        ? numChart(pd.studentCount)
        : classDim
          ? numChart(pd.classCount)
          : numChart(pd.amount ?? pd.receivedAmount)
      periodMap[pd.period] = v
    })
    return {
      orgId: item.orgId,
      orgName: item.orgName,
      primaryCount: periodMap['1'] ?? 0,
      juniorCount: periodMap['2'] ?? 0,
      seniorCount: periodMap['3'] ?? 0
    }
  })
}

const applyRemainingHoursData = (raw) => {
  remainingHoursChartData.value = (raw || []).map(item => ({
    orgId: item.orgId,
    orgName: item.orgName,
    remainingAmount: numChart(item.remainingAmount),
    remainingHours: numChart(item.remainingHours)
  }))
}
const enrollOrgLegendSelected = ref({
  '报名应收': true,
  '退课应退': true,
  '学费核销': true
})

// 图例配置
const subjectLegend = ['小学', '初中', '高中']
const subjectColors = ['#52c41a', '#faad14', '#f5222d']
const courseTypeLegend = ['班课', '1对1']
const courseTypeColors = ['#3478f6', '#ff9f43']
const seasonLegend = ['春季', '暑期', '秋季', '寒假']
const seasonColors = ['#91CC75', '#FAC858', '#5470C6', '#EE6666']
const signUpCategoryLegend = ['新签', '扩科', '续费']
const signUpCategoryColors = ['#3478f6', '#52c41a', '#faad14']
const classStudentLegend = ['小学', '初中', '高中']
const classStudentColors = ['#52c41a', '#faad14', '#f5222d']
// 报课分校区统计图例
const enrollOrgLegend = ['报名应收', '退课应退', '学费核销']
const enrollOrgColors = ['#36b371', '#ff7875', '#3478f6']
// ========== 每个图表独立控制维度 ==========
const CHART_DIM_AMOUNT = 'amount'
const CHART_DIM_PEOPLE = 'people'
/** 实时班课在班统计：班级维度（periodDetails.classCount） */
const CHART_DIM_CLASS = 'class'
const chartDimensionOptions = [
  { label: '金额', value: CHART_DIM_AMOUNT },
  { label: '人数', value: CHART_DIM_PEOPLE }
]
const classStudentChartDimensionOptions = [
  { label: '人数', value: CHART_DIM_PEOPLE },
  { label: '班级', value: CHART_DIM_CLASS }
]
const remainingHoursChartDimensionOptions = [
  { label: '剩余金额', value: CHART_DIM_AMOUNT },
  { label: '剩余课时', value: 'hours' }
]

// ========== 每个图表独立的维度控制 ==========
const branchChartDimension = ref(CHART_DIM_AMOUNT)
const subjectChartDimension = ref(CHART_DIM_AMOUNT)
const courseTypeChartDimension = ref(CHART_DIM_AMOUNT)
const seasonChartDimension = ref(CHART_DIM_AMOUNT)
const signUpCategoryChartDimension = ref(CHART_DIM_AMOUNT)
const classStudentChartDimension = ref(CHART_DIM_PEOPLE)
const remainingHoursChartDimension = ref(CHART_DIM_AMOUNT)

const handleBranchChartDimensionChange = async (v) => {
  if (branchChartDimension.value === v) return
  branchChartDimension.value = v
  if (rawChartData.value.branch[v]) {
    applyBranchData(rawChartData.value.branch[v], v)
    renderCurrentChart('branch')
  } else if (!hasLoadedCharts.value) {
    await loadAllChartDimensions()
  }
}

const handleSubjectChartDimensionChange = async (v) => {
  if (subjectChartDimension.value === v) return
  subjectChartDimension.value = v
  if (rawChartData.value.subject[v]) {
    applySubjectData(rawChartData.value.subject[v], v)
    renderCurrentChart('subject')
  } else if (!hasLoadedCharts.value) {
    await loadAllChartDimensions()
  }
}

const handleCourseTypeChartDimensionChange = async (v) => {
  if (courseTypeChartDimension.value === v) return
  courseTypeChartDimension.value = v
  if (rawChartData.value.courseType[v]) {
    applyCourseTypeData(rawChartData.value.courseType[v], v)
    renderCurrentChart('courseType')
  } else if (!hasLoadedCharts.value) {
    await loadAllChartDimensions()
  }
}

const handleSeasonChartDimensionChange = async (v) => {
  if (seasonChartDimension.value === v) return
  seasonChartDimension.value = v
  if (rawChartData.value.season[v]) {
    applySeasonData(rawChartData.value.season[v], v)
    renderCurrentChart('season')
  } else if (!hasLoadedCharts.value) {
    await loadAllChartDimensions()
  }
}

const handleSignUpCategoryChartDimensionChange = async (v) => {
  if (signUpCategoryChartDimension.value === v) return
  signUpCategoryChartDimension.value = v
  if (rawChartData.value.signUpCategory[v]) {
    applySignUpCategoryData(rawChartData.value.signUpCategory[v], v)
    renderCurrentChart('signUpCategory')
  } else if (!hasLoadedCharts.value) {
    await loadAllChartDimensions()
  }
}

const handleClassStudentChartDimensionChange = async (v) => {
  if (classStudentChartDimension.value === v) return
  classStudentChartDimension.value = v
  console.log('[班级维度切换] 切换到:', v, '| 缓存是否存在:', !!rawChartData.value.classStudent[v], '| hasLoadedCharts:', hasLoadedCharts.value)
  if (rawChartData.value.classStudent[v]) {
    applyClassStudentData(rawChartData.value.classStudent[v], v)
    renderCurrentChart('classStudent')
  } else if (!hasLoadedCharts.value) {
    console.log('[班级维度切换] 缓存不存在，调用 loadAllChartDimensions')
    await loadAllChartDimensions()
  } else {
    console.log('[班级维度切换] 已加载完毕但缓存缺失，强制请求班级维度')
    await loadMissingDimension(v)
  }
}

// 补充缺失维度的单独请求
const loadMissingDimension = async (dim) => {
  const orgId = getOrgId()
  const startDate = currentDateRange.value[0]
  const endDate = currentDateRange.value[1]
  try {
    const res = await getClassStudentCountStatistics({ orgId, startDate, endDate })
    if (res.code === '00' && res.data) {
      const d = res.data
      rawChartData.value.classStudent[dim] = d
      console.log('[loadMissingDimension] 数据已缓存，开始 applyClassStudentData', dim)
      applyClassStudentData(d, dim)
      renderCurrentChart('classStudent')
    }
  } catch (error) {
    console.error('[loadMissingDimension] 请求失败:', error)
  }
}

const handleRemainingHoursChartDimensionChange = async (v) => {
  if (remainingHoursChartDimension.value === v) return
  remainingHoursChartDimension.value = v
  // 剩余课时/金额均来自 amount 维度的同一份接口，无需重新请求，直接重渲染
  renderCurrentChart('remainingHours')
}

const getBranchLegendKeys = () => ({
  income: branchChartDimension.value === CHART_DIM_PEOPLE ? '实收人数' : '实收金额',
  refund: branchChartDimension.value === CHART_DIM_PEOPLE ? '退费人数' : '实退金额'
})

const branchLegend = computed(() => {
  const k = getBranchLegendKeys()
  return [k.income, k.refund]
})
const branchColors = ['#52c41a', '#ff7875']

// ========== 图例联动：分组柱状图下图例切换直接隐藏/显示系列 ==========

// 分校区图例选中状态（系列名随维度变化，在 loadBranchRevenueCharts 成功时重置）
const branchLegendSelected = ref({ '实收金额': true, '实退金额': true })

// 分校区图例选择变化事件（分组柱：ECharts 内置图例已自动控制显隐，无需手动 setOption）
const onBranchLegendSelectChanged = (e) => {
  if (!isAllOrg.value || !branchChartInstance) return
  branchLegendSelected.value = { ...branchLegendSelected.value, ...(e.selected || {}) }
}

// 学部图例选中状态
const subjectLegendSelected = ref({ '小学': true, '初中': true, '高中': true })

// 计算学部可见系列总和
const getSubjectVisibleTotal = (row, sel) => {
  const p = Number(row.primaryAmount) || 0
  const j = Number(row.juniorAmount) || 0
  const s = Number(row.seniorAmount) || 0
  let sum = 0
  if (sel['小学'] !== false) sum += p
  if (sel['初中'] !== false) sum += j
  if (sel['高中'] !== false) sum += s
  return sum
}

// 获取学部最上层可见段（堆叠顺序：小学(下) → 初中 → 高中(上)）
const getSubjectTopSegment = (row, sel) => {
  const p = Number(row.primaryAmount) || 0
  const j = Number(row.juniorAmount) || 0
  const s = Number(row.seniorAmount) || 0
  const showS = sel['高中'] !== false && s > 0
  const showJ = sel['初中'] !== false && j > 0
  const showP = sel['小学'] !== false && p > 0
  if (showS) return 'senior'
  if (showJ) return 'junior'
  if (showP) return 'primary'
  return null
}

// 学部标签格式化函数（layer: 'primary' | 'junior' | 'senior'）
const subjectLabelFormatter = (layer) => (params) => {
  const row = subjectChartData.value[params.dataIndex]
  if (!row) return ''
  const sel = subjectLegendSelected.value
  const sum = getSubjectVisibleTotal(row, sel)
  if (!sum) return ''
  const top = getSubjectTopSegment(row, sel)
  const fmt = subjectChartDimension.value === CHART_DIM_PEOPLE ? formatCountLabel : formatWanLabel
  if (layer === 'senior') return top === 'senior' ? fmt(sum) : ''
  if (layer === 'junior') return top === 'junior' ? fmt(sum) : ''
  if (layer === 'primary') return top === 'primary' ? fmt(sum) : ''
  return ''
}

// 课程类型图例选中状态
const courseTypeLegendSelected = ref({ '班课': true, '1对1': true })

// 计算课程类型可见系列总和
const getCourseTypeVisibleTotal = (row, sel) => {
  const c = Number(row.classAmount) || 0
  const o = Number(row.oneToOneAmount) || 0
  let sum = 0
  if (sel['班课'] !== false) sum += c
  if (sel['1对1'] !== false) sum += o
  return sum
}

// 获取课程类型最上层可见段（堆叠顺序：班课(下) → 1对1(上)）
const getCourseTypeTopSegment = (row, sel) => {
  const c = Number(row.classAmount) || 0
  const o = Number(row.oneToOneAmount) || 0
  const showO = sel['1对1'] !== false && o > 0
  const showC = sel['班课'] !== false && c > 0
  if (showO) return 'oneToOne'
  if (showC) return 'class'
  return null
}

// 课程类型标签格式化函数（isOneToOneSeries: boolean）
const courseTypeLabelFormatter = (isOneToOneSeries) => (params) => {
  const row = courseTypeChartData.value[params.dataIndex]
  if (!row) return ''
  const sel = courseTypeLegendSelected.value
  const sum = getCourseTypeVisibleTotal(row, sel)
  if (!sum) return ''
  const top = getCourseTypeTopSegment(row, sel)
  const fmt = courseTypeChartDimension.value === CHART_DIM_PEOPLE ? formatCountLabel : formatWanLabel
  if (isOneToOneSeries) return top === 'oneToOne' ? fmt(sum) : ''
  return top === 'class' ? fmt(sum) : ''
}

// 季节图例选中状态
const seasonLegendSelected = ref({ '春季': true, '暑期': true, '秋季': true, '寒假': true })

// 计算季节可见系列总和
const getSeasonVisibleTotal = (row, sel) => {
  const sp = Number(row.springAmount) || 0
  const su = Number(row.summerAmount) || 0
  const au = Number(row.autumnAmount) || 0
  const wi = Number(row.winterAmount) || 0
  let sum = 0
  if (sel['春季'] !== false) sum += sp
  if (sel['暑期'] !== false) sum += su
  if (sel['秋季'] !== false) sum += au
  if (sel['寒假'] !== false) sum += wi
  return sum
}

// 获取季节最上层可见段（堆叠顺序：春季(下) → 暑期 → 秋季 → 寒假(上)）
const getSeasonTopSegment = (row, sel) => {
  const sp = Number(row.springAmount) || 0
  const su = Number(row.summerAmount) || 0
  const au = Number(row.autumnAmount) || 0
  const wi = Number(row.winterAmount) || 0
  const showWi = sel['寒假'] !== false && wi > 0
  const showAu = sel['秋季'] !== false && au > 0
  const showSu = sel['暑期'] !== false && su > 0
  const showSp = sel['春季'] !== false && sp > 0
  if (showWi) return 'winter'
  if (showAu) return 'autumn'
  if (showSu) return 'summer'
  if (showSp) return 'spring'
  return null
}

// ========== 报课分校区统计辅助函数 ==========
// 计算报课分校区可见系列总和（退课应退+学费核销）
const getEnrollOrgVisibleTotal = (row, sel) => {
  const refund = Number(row.refundPayableAmount) || 0
  const consume = Number(row.lessonConsumeAmount) || 0
  let sum = 0
  if (sel['退课应退'] !== false) sum += refund
  if (sel['学费核销'] !== false) sum += consume
  return sum
}

// 获取报课分校区最上层可见段（堆叠顺序：退课应退(下) → 学费核销(上)）
const getEnrollOrgTopSegment = (row, sel) => {
  const refund = Number(row.refundPayableAmount) || 0
  const consume = Number(row.lessonConsumeAmount) || 0
  const showConsume = sel['学费核销'] !== false && consume > 0
  const showRefund = sel['退课应退'] !== false && refund > 0
  if (showConsume) return 'consume'
  if (showRefund) return 'refund'
  return null
}

// 报课分校区标签格式化函数
const enrollOrgLabelFormatter = (layer) => (params) => {
  const row = enrollOrgChartData.value[params.dataIndex]
  if (!row) return ''
  const sel = enrollOrgLegendSelected.value
  const sum = getEnrollOrgVisibleTotal(row, sel)
  // 报名应收（独立柱）：始终显示
  if (layer === 'enroll') {
    return formatWanLabel(row.enrollPayableAmount || 0)
  }
  // 堆叠柱：学费核销在最上层
  if (layer === 'consume') {
    const consume = Number(row.lessonConsumeAmount) || 0
    if (sel['学费核销'] !== false && consume > 0) {
      return formatWanLabel(sum)
    }
    return ''
  }
  // 堆叠柱：退课应退在底层（学费核销不显示时）
  if (layer === 'refund') {
    const refund = Number(row.refundPayableAmount) || 0
    if (sel['退课应退'] !== false && refund > 0 && sum === refund) {
      return formatWanLabel(sum)
    }
    return ''
  }
  return ''
}

// 图例选择变化事件处理函数
const onEnrollOrgLegendSelectChanged = (e) => {
  if (!isAllOrg.value || !enrollOrgChartInstance) return
  enrollOrgLegendSelected.value = { ...enrollOrgLegendSelected.value, ...(e.selected || {}) }
  // 更新系列标签格式化函数
  enrollOrgChartInstance.setOption({
    series: [
      { label: { formatter: enrollOrgLabelFormatter('enroll') } },
      { label: { formatter: enrollOrgLabelFormatter('refund') } },
      { label: { formatter: enrollOrgLabelFormatter('consume') } }
    ]
  }, false)
}

// 季节标签格式化函数
const seasonLabelFormatter = (layer) => (params) => {
  const row = seasonChartData.value[params.dataIndex]
  if (!row) return ''
  const sel = seasonLegendSelected.value
  const sum = getSeasonVisibleTotal(row, sel)
  if (!sum) return ''
  const top = getSeasonTopSegment(row, sel)
  const fmt = seasonChartDimension.value === CHART_DIM_PEOPLE ? formatCountLabel : formatWanLabel
  if (layer === 'winter') return top === 'winter' ? fmt(sum) : ''
  if (layer === 'autumn') return top === 'autumn' ? fmt(sum) : ''
  if (layer === 'summer') return top === 'summer' ? fmt(sum) : ''
  if (layer === 'spring') return top === 'spring' ? fmt(sum) : ''
  return ''
}

// 报名分类图例选中状态
const signUpCategoryLegendSelected = ref({ '新签': true, '扩科': true, '续费': true })

// 计算报名分类可见系列总和
const getSignUpCategoryVisibleTotal = (row, sel) => {
  const n = Number(row.newSignCount) || 0
  const e = Number(row.expandCount) || 0
  const r = Number(row.renewCount) || 0
  let sum = 0
  if (sel['新签'] !== false) sum += n
  if (sel['扩科'] !== false) sum += e
  if (sel['续费'] !== false) sum += r
  return sum
}

// 获取报名分类最上层可见段（堆叠顺序：新签(下) → 扩科 → 续费(上)）
const getSignUpCategoryTopSegment = (row, sel) => {
  const n = Number(row.newSignCount) || 0
  const e = Number(row.expandCount) || 0
  const r = Number(row.renewCount) || 0
  const showR = sel['续费'] !== false && r > 0
  const showE = sel['扩科'] !== false && e > 0
  const showN = sel['新签'] !== false && n > 0
  if (showR) return 'renew'
  if (showE) return 'expand'
  if (showN) return 'newSign'
  return null
}

// 报名分类标签格式化函数
const signUpCategoryLabelFormatter = (layer) => (params) => {
  const row = signUpCategoryChartData.value[params.dataIndex]
  if (!row) return ''
  const sel = signUpCategoryLegendSelected.value
  const sum = getSignUpCategoryVisibleTotal(row, sel)
  if (!sum) return ''
  const top = getSignUpCategoryTopSegment(row, sel)
  const fmt = signUpCategoryChartDimension.value === CHART_DIM_PEOPLE ? formatCountLabel : formatMoney
  if (layer === 'renew') return top === 'renew' ? fmt(sum) : ''
  if (layer === 'expand') return top === 'expand' ? fmt(sum) : ''
  if (layer === 'newSign') return top === 'newSign' ? fmt(sum) : ''
  return ''
}

// 实时班课在班人数图例选中状态
const classStudentLegendSelected = ref({ '小学': true, '初中': true, '高中': true })

// 计算实时班课可见系列总和
const getClassStudentVisibleTotal = (row, sel) => {
  const p = Number(row.primaryCount) || 0
  const j = Number(row.juniorCount) || 0
  const s = Number(row.seniorCount) || 0
  let sum = 0
  if (sel['小学'] !== false) sum += p
  if (sel['初中'] !== false) sum += j
  if (sel['高中'] !== false) sum += s
  return sum
}

// 获取实时班课最上层可见段
const getClassStudentTopSegment = (row, sel) => {
  const p = Number(row.primaryCount) || 0
  const j = Number(row.juniorCount) || 0
  const s = Number(row.seniorCount) || 0
  const showS = sel['高中'] !== false && s > 0
  const showJ = sel['初中'] !== false && j > 0
  const showP = sel['小学'] !== false && p > 0
  if (showS) return 'senior'
  if (showJ) return 'junior'
  if (showP) return 'primary'
  return null
}

const classStudentIsCountDimension = () => {
  const d = classStudentChartDimension.value
  return d === CHART_DIM_PEOPLE || d === CHART_DIM_CLASS
}

// 实时班课标签格式化函数
const classStudentLabelFormatter = (layer) => (params) => {
  const row = classStudentChartData.value[params.dataIndex]
  if (!row) return ''
  const sel = classStudentLegendSelected.value
  const sum = getClassStudentVisibleTotal(row, sel)
  if (!sum) return ''
  const top = getClassStudentTopSegment(row, sel)
  const fmt = classStudentIsCountDimension() ? formatCountLabel : formatMoney
  if (layer === 'senior') return top === 'senior' ? fmt(sum) : ''
  if (layer === 'junior') return top === 'junior' ? fmt(sum) : ''
  if (layer === 'primary') return top === 'primary' ? fmt(sum) : ''
  return ''
}

// 图例选择变化事件处理函数
const onSubjectLegendSelectChanged = (e) => {
  if (!isAllOrg.value || !subjectChartInstance) return
  subjectLegendSelected.value = { ...subjectLegendSelected.value, ...(e.selected || {}) }
  subjectChartInstance.setOption({
    series: [
      { label: { formatter: subjectLabelFormatter('primary') } },
      { label: { formatter: subjectLabelFormatter('junior') } },
      { label: { formatter: subjectLabelFormatter('senior') } }
    ]
  }, false)
}

const onCourseTypeLegendSelectChanged = (e) => {
  if (!isAllOrg.value || !courseTypeChartInstance) return
  courseTypeLegendSelected.value = { ...courseTypeLegendSelected.value, ...(e.selected || {}) }
  courseTypeChartInstance.setOption({
    series: [
      { label: { formatter: courseTypeLabelFormatter(false) } },
      { label: { formatter: courseTypeLabelFormatter(true) } }
    ]
  }, false)
}

const onSeasonLegendSelectChanged = (e) => {
  if (!isAllOrg.value || !seasonChartInstance) return
  seasonLegendSelected.value = { ...seasonLegendSelected.value, ...(e.selected || {}) }
  seasonChartInstance.setOption({
    series: [
      { label: { formatter: seasonLabelFormatter('spring') } },
      { label: { formatter: seasonLabelFormatter('summer') } },
      { label: { formatter: seasonLabelFormatter('autumn') } },
      { label: { formatter: seasonLabelFormatter('winter') } }
    ]
  }, false)
}

const onSignUpCategoryLegendSelectChanged = (e) => {
  if (!isAllOrg.value || !signUpCategoryChartInstance) return
  signUpCategoryLegendSelected.value = { ...signUpCategoryLegendSelected.value, ...(e.selected || {}) }
  signUpCategoryChartInstance.setOption({
    series: [
      { label: { formatter: signUpCategoryLabelFormatter('newSign') } },
      { label: { formatter: signUpCategoryLabelFormatter('expand') } },
      { label: { formatter: signUpCategoryLabelFormatter('renew') } }
    ]
  }, false)
}

const onClassStudentLegendSelectChanged = (e) => {
  if (!isAllOrg.value || !classStudentChartInstance) return
  classStudentLegendSelected.value = { ...classStudentLegendSelected.value, ...(e.selected || {}) }
  classStudentChartInstance.setOption({
    series: [
      { label: { formatter: classStudentLabelFormatter('primary') } },
      { label: { formatter: classStudentLabelFormatter('junior') } },
      { label: { formatter: classStudentLabelFormatter('senior') } }
    ]
  }, false)
}

// ========== 图例联动 END ==========

// 图表实例
const branchChart = ref(null)
const subjectChart = ref(null)
const courseTypeChart = ref(null)
const seasonChart = ref(null)
const signUpCategoryChart = ref(null)
const classStudentChart = ref(null)
const enrollOrgChart = ref(null)
const remainingHoursChart = ref(null)
let branchChartInstance = null
let subjectChartInstance = null
let courseTypeChartInstance = null
let seasonChartInstance = null
let signUpCategoryChartInstance = null
let classStudentChartInstance = null
let enrollOrgChartInstance = null
let remainingHoursChartInstance = null

// 判断是否选择了所有校区
const isAllOrg = computed(() => selectedOrg.value === 'all')

/** 单校区图表取首行（接口通常只返回当前校一条） */
const getFirstChartRow = (list) => (Array.isArray(list) && list.length ? list[0] : {})

const singleDimBarLabel = {
  show: true,
  position: 'top',
  fontSize: 12,
  color: '#333',
  fontWeight: 600
}

// PC端图表相关配置：视口 ≥768px 视为 PC，柱图不倾斜标签且柱宽用固定像素
const xAxisCatRotate = () => (isPC.value ? 0 : 20)
const barTopLabelRotate = () => (isPC.value ? 0 : 30)
const PC_BAR_W_GROUP = 28
const PC_BAR_W_SINGLE = 32
const barWidthGrouped = () => (isPC.value ? PC_BAR_W_GROUP : '32%')
const barWidthStack = () => (isPC.value ? PC_BAR_W_GROUP : '35%')
const barWidthSingleCol = () => (isPC.value ? PC_BAR_W_SINGLE : '44%')
const barWidthSeasonSingle = () => (isPC.value ? PC_BAR_W_SINGLE : '36%')

/** 图表金额标签：最多两位小数，去掉末尾无意义的 0 */
const formatWanLabel = (v) => {
  const n = Number(v)
  if (!Number.isFinite(n)) return ''
  return String(parseFloat(n.toFixed(2)))
}

const formatCountLabel = (v) => {
  const n = Number(v)
  if (!Number.isFinite(n)) return ''
  return String(Math.round(n))
}

const hasAnyData = computed(() => {
  return branchChartData.value.length > 0 || subjectChartData.value.length > 0 ||
    courseTypeChartData.value.length > 0 || seasonChartData.value.length > 0 ||
    signUpCategoryChartData.value.length > 0 || classStudentChartData.value.length > 0
})

// 格式化金额（卡片与部分图表；与柱图标签一致，去掉末尾无意义的 0）
const formatMoney = (value) => {
  if (value !== 0 && !value) return '0'
  const num = parseFloat(value)
  if (!Number.isFinite(num)) return '0'
  return String(parseFloat(num.toFixed(2)))
}

// 获取校区ID
const getOrgId = () => {
  return selectedOrg.value === 'all' ? '' : selectedOrg.value
}

const loadRevenueCards = async () => {
  const orgId = getOrgId()
  const startDate = currentDateRange.value[0]
  const endDate = currentDateRange.value[1]

  try {
    const res = await getRevenueStatistics({ orgId, startDate, endDate })
    if (res.code === '00' && res.data) {
      const cm = res.data.currentMonthData || {}
      const pd = res.data.periodData || {}
      monthOverview.value = {
        currentIncome: cm.receivedAmount ?? 0,
        currentRefund: cm.refundAmount ?? 0
      }
      periodStats.value = {
        actualIncome: pd.receivedAmount ?? 0,
        actualRefund: pd.refundAmount ?? 0,
        signUpCount: pd.enrollmentCount ?? 0,
        refundCount: pd.refundCount ?? 0
      }
    } else {
      monthOverview.value = { currentIncome: 0, currentRefund: 0 }
      periodStats.value = { actualIncome: 0, actualRefund: 0, signUpCount: 0, refundCount: 0 }
    }
  } catch (error) {
    console.error('加载营收卡片失败:', error)
    monthOverview.value = { currentIncome: 0, currentRefund: 0 }
    periodStats.value = { actualIncome: 0, actualRefund: 0, signUpCount: 0, refundCount: 0 }
  }
}

const numChart = (v) => {
  const n = Number(v)
  return Number.isFinite(n) ? n : 0
}

const loadAllChartDimensions = async () => {
  // 防重入：如果已经在加载中，直接返回
  // if (loadAllChartDimensions.running) return
  // loadAllChartDimensions.running = true

  const orgId = getOrgId()
  const startDate = currentDateRange.value[0]
  const endDate = currentDateRange.value[1]
  const params = { orgId, startDate, endDate }

  // 缓存当前选中维度，加载完成后只渲染当前维度的图表
  const chartDimensionMap = {
    branch: branchChartDimension.value,
    subject: subjectChartDimension.value,
    courseType: courseTypeChartDimension.value,
    season: seasonChartDimension.value,
    signUpCategory: signUpCategoryChartDimension.value,
    classStudent: classStudentChartDimension.value
  }

  // 缺少哪个维度的缓存，就单独请求对应维度
  const needLoadDimensions = Object.values(chartDimensionMap).filter(
    dim => !rawChartData.value.classStudent[dim]
  )
  const uniqueDimensions = [...new Set(needLoadDimensions)]

  console.log('uniqueDimensions', uniqueDimensions)

  try {
    const [
      branchRes,
      classStudentRes,
      signUpCategoryRes,
      subjectRes,
      courseTypeRes,
      seasonRes,
      remainingHoursRes,
      enrollOrgRes
    ] = await Promise.all([
      getOrgAmountStatistics(params),
      getClassStudentCountStatistics(params),
      getOrderTypeAmountStatistics(params),
      getPeriodAmountStatistics(params),
      getCourseTypeAmountStatistics(params),
      getSemesterAmountStatistics(params),
      getRemainingHoursStatistics(params),
      getOrderOrgStatistics(params)
    ])

    if (branchRes.code === '00' && branchRes.data) {
      const d = branchRes.data
      for (const dim of uniqueDimensions) {
        rawChartData.value.branch[dim] = d
      }
      applyBranchData(d, chartDimensionMap.branch)
    }

    if (classStudentRes.code == '00' && classStudentRes.data) {
      const d = classStudentRes.data
      for (const dim of uniqueDimensions) {
        rawChartData.value.classStudent[dim] = d
      }
      applyClassStudentData(d, chartDimensionMap.classStudent)
    }

    if (signUpCategoryRes.code === '00' && signUpCategoryRes.data) {
      const d = signUpCategoryRes.data
      for (const dim of uniqueDimensions) {
        rawChartData.value.signUpCategory[dim] = d
      }
      applySignUpCategoryData(d, chartDimensionMap.signUpCategory)
    }

    if (subjectRes.code === '00' && subjectRes.data) {
      const d = subjectRes.data
      for (const dim of uniqueDimensions) {
        rawChartData.value.subject[dim] = d
      }
      applySubjectData(d, chartDimensionMap.subject)
    }

    if (courseTypeRes.code === '00' && courseTypeRes.data) {
      const d = courseTypeRes.data
      for (const dim of uniqueDimensions) {
        rawChartData.value.courseType[dim] = d
      }
      applyCourseTypeData(d, chartDimensionMap.courseType)
    }

    if (seasonRes.code === '00' && seasonRes.data) {
      const d = seasonRes.data
      for (const dim of uniqueDimensions) {
        rawChartData.value.season[dim] = d
      }
      applySeasonData(d, chartDimensionMap.season)
    }

    if (remainingHoursRes.code === '00' && remainingHoursRes.data) {
      const d = remainingHoursRes.data
      const remainingHoursList = Array.isArray(d) ? d : (d ? [d] : [])
      for (const dim of uniqueDimensions) {
        rawChartData.value.remainingHours[dim] = remainingHoursList
      }
      applyRemainingHoursData(remainingHoursList)
    }

    if (enrollOrgRes.code === '00' && enrollOrgRes.data) {
      const d = enrollOrgRes.data
      const enrollOrgList = Array.isArray(d) ? d : (d ? [d] : [])
      enrollOrgChartData.value = enrollOrgList.map(item => ({
        orgId: item.orgId,
        orgName: item.orgName,
        enrollPayableAmount: numChart(item.enrollPayableAmount),
        refundPayableAmount: numChart(item.refundPayableAmount),
        lessonConsumeAmount: numChart(item.lessonConsumeAmount)
      }))
    }
  } catch (error) {
    console.error(`加载图表数据失败:`, error)
  }

  hasLoadedCharts.value = true

  nextTick(() => {
    renderBranchChart()
    renderSubjectChart()
    renderCourseTypeChart()
    renderSeasonChart()
    renderSignUpCategoryChart()
    renderClassStudentChart()
    renderEnrollOrgChart()
    renderRemainingHoursChart()
  })
}

const loadBranchRevenueCharts = async () => {
  await loadAllChartDimensions()
}

// 渲染分校区图（全校区：X 轴为各校区，两系列分组柱；单校区：X 轴为实收/实退维度，独立柱）
const renderBranchChart = () => {
  if (!branchChart.value) return
  if (branchChartData.value.length === 0) {
    branchChartInstance?.clear()
    return
  }

  if (!branchChartInstance) {
    branchChartInstance = echarts.init(branchChart.value)
    branchChartInstance.on('legendselectchanged', onBranchLegendSelectChanged)
  }

  if (!isAllOrg.value) {
    const k = getBranchLegendKeys()
    branchLegendSelected.value = { [k.income]: true, [k.refund]: true }
    const row = getFirstChartRow(branchChartData.value)
    const income = (row.income || 0)
    const refund = (row.refund || 0)
    const xDims = [k.income, k.refund]
    const dimPeople = branchChartDimension.value === CHART_DIM_PEOPLE

    branchChartInstance.setOption({
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        borderColor: '#e8e8e8',
        borderWidth: 1,
        textStyle: { color: '#333' },
        extraCssText: 'text-align: left;',
        formatter: (params) => {
          const p = params[0]
          return dimPeople
            ? `${p.name}<br/>人数: ${formatCountLabel(p.value)}人`
            : `${p.name}<br/>金额: ${formatWanLabel(p.value)}元`
        }
      },
      legend: { show: false },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '12%',
        top: '12%',
        containLabel: true
      },
      xAxis: {
        type: 'category',
        data: xDims,
        axisLine: { lineStyle: { color: '#e8e8e8' } },
        axisLabel: { fontSize: 12, color: '#666' },
        axisTick: { show: false }
      },
      yAxis: {
        type: 'value',
        name: '',
        nameTextStyle: { color: '#999', fontSize: 11 },
        axisLabel: { fontSize: 11, color: '#999' },
        axisLine: { show: false },
        splitLine: { lineStyle: { color: '#f0f0f0', type: 'dashed' } }
      },
      series: [
        {
          type: 'bar',
          name: '金额',
          barWidth: barWidthSingleCol(),
          data: [
            {
              value: income,
              itemStyle: {
                color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                  { offset: 0, color: '#36b371' },
                  { offset: 1, color: '#22B894' }
                ]),
                borderRadius: [4, 4, 0, 0]
              }
            },
            {
              value: refund,
              itemStyle: {
                color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                  { offset: 0, color: '#ff7875' },
                  { offset: 1, color: '#EE6666' }
                ]),
                borderRadius: [4, 4, 0, 0]
              }
            }
          ],
          label: {
            ...singleDimBarLabel,
            formatter: (p) =>
              dimPeople ? formatCountLabel(p.value) : formatWanLabel(p.value)
          }
        }
      ]
    }, { notMerge: true })
    return
  }

  const xData = branchChartData.value.map(item => item.orgName)
  const incomeData = branchChartData.value.map(item => item.income)
  const refundData = branchChartData.value.map(item => item.refund)
  const bk = getBranchLegendKeys()
  const dimPeopleAll = branchChartDimension.value === CHART_DIM_PEOPLE

  branchChartInstance.setOption({
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      backgroundColor: 'rgba(255, 255, 255, 0.95)',
      borderColor: '#e8e8e8',
      borderWidth: 1,
      textStyle: { color: '#333' },
      extraCssText: 'text-align: left;',
      formatter: function(params) {
        const groupIdx = params[0]?.dataIndex ?? 0
        const row = branchChartData.value[groupIdx]
        const sel = branchLegendSelected.value
        const k = getBranchLegendKeys()
        const incVis = sel[k.income] !== false
        const refVis = sel[k.refund] !== false
        let result = row?.orgName + '<br/>'
        params.forEach(item => {
          const v = dimPeopleAll ? formatCountLabel(item.value) : formatWanLabel(item.value)
          result += item.marker + item.seriesName + ': ' + v + (dimPeopleAll ? '人' : '元') + '<br/>'
        })
        const income = Number(row?.income) || 0
        const refund = Number(row?.refund) || 0
        const incAmt = incVis ? income : 0
        const refAmt = refVis ? refund : 0
        const net = dimPeopleAll ? incAmt + refAmt : incAmt - refAmt
        const netStr = dimPeopleAll ? formatCountLabel(net) : formatWanLabel(net)
        if (dimPeopleAll) {
          return result
        }
        result += '<hr style="margin: 3px 0">净额: ' + netStr + (dimPeopleAll ? '人' : '元')
        return result
      }
    },
    legend: {
      data: [bk.income, bk.refund],
      selected: { [bk.income]: true, [bk.refund]: true },
      bottom: 5,
      itemWidth: 14,
      itemHeight: 14,
      textStyle: { fontSize: 12, color: '#666' },
      itemGap: 20
    },
    grid: {
      left: '3%',
      right: '4%',
      bottom: '18%',
      top: '12%',
      containLabel: true
    },
    xAxis: {
      type: 'category',
      data: xData,
      axisLabel: {
        fontSize: 12,
        color: '#666',
        rotate: xAxisCatRotate(),
        interval: 0
      },
      axisLine: { lineStyle: { color: '#e8e8e8' } },
      axisTick: { show: false }
    },
    yAxis: {
      type: 'value',
      name: '',
      nameTextStyle: { color: '#999', fontSize: 11 },
      axisLabel: { fontSize: 11, color: '#999' },
      axisLine: { show: false },
      splitLine: { lineStyle: { color: '#f0f0f0', type: 'dashed' } }
    },
    series: [
      {
        name: bk.income,
        type: 'bar',
        barWidth: barWidthGrouped(),
        barGap: '10%',
        data: incomeData,
        itemStyle: {
          color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
            { offset: 0, color: '#36b371' },
            { offset: 1, color: '#22B894' }
          ]),
          borderRadius: [4, 4, 0, 0]
        },
        label: {
          show: true,
          position: 'top',
          rotate: barTopLabelRotate(),
          fontSize: 12,
          fontWeight: 600,
          color: '#333',
          formatter: (p) => dimPeopleAll ? formatCountLabel(p.value) : formatWanLabel(p.value)
        }
      },
      {
        name: bk.refund,
        type: 'bar',
        barWidth: barWidthGrouped(),
        data: refundData,
        itemStyle: {
          color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
            { offset: 0, color: '#ff7875' },
            { offset: 1, color: '#EE6666' }
          ]),
          borderRadius: [4, 4, 0, 0]
        },
        label: {
          show: true,
          position: 'top',
          rotate: barTopLabelRotate(),
          fontSize: 12,
          fontWeight: 600,
          color: '#333',
          formatter: (p) => dimPeopleAll ? formatCountLabel(p.value) : formatWanLabel(p.value)
        }
      }
    ]
  }, { notMerge: true })
}

// 渲染学部统计图（全校区：按校区堆叠；单校区：X 轴为小学/初中/高中，独立柱）
const renderSubjectChart = () => {
  if (!subjectChart.value) return
  if (subjectChartData.value.length === 0) {
    subjectChartInstance?.clear()
    return
  }

  if (!subjectChartInstance) {
    subjectChartInstance = echarts.init(subjectChart.value)
    subjectChartInstance.on('legendselectchanged', onSubjectLegendSelectChanged)
  }

  if (!isAllOrg.value) {
    subjectLegendSelected.value = { '小学': true, '初中': true, '高中': true }
    const row = getFirstChartRow(subjectChartData.value)
    const p = (row.primaryAmount || 0)
    const j = (row.juniorAmount || 0)
    const s = (row.seniorAmount || 0)
    const dimPeople = subjectChartDimension.value === CHART_DIM_PEOPLE

    subjectChartInstance.setOption({
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        borderColor: '#e8e8e8',
        borderWidth: 1,
        textStyle: { color: '#333' },
        extraCssText: 'text-align: left;',
        formatter: (params) => {
          const pt = params[0]
          return dimPeople
            ? `${pt.name}<br/>人数: ${formatCountLabel(pt.value)}人`
            : `${pt.name}<br/>金额: ${formatWanLabel(pt.value)}元`
        }
      },
      legend: { show: false },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '12%',
        top: '12%',
        containLabel: true
      },
      xAxis: {
        type: 'category',
        data: [...subjectLegend],
        axisLabel: { fontSize: 12, color: '#666' },
        axisLine: { lineStyle: { color: '#e8e8e8' } },
        axisTick: { show: false }
      },
      yAxis: {
        type: 'value',
        name: '',
        nameTextStyle: { color: '#999', fontSize: 11 },
        axisLabel: { fontSize: 11, color: '#999' },
        axisLine: { show: false },
        splitLine: { lineStyle: { color: '#f0f0f0', type: 'dashed' } }
      },
      series: [
        {
          type: 'bar',
          name: '学部',
          barWidth: barWidthSingleCol(),
          barCategoryGap: '20px',
          data: [
            { value: p, itemStyle: { color: '#52c41a', borderRadius: [4, 4, 0, 0] } },
            { value: j, itemStyle: { color: '#faad14', borderRadius: [4, 4, 0, 0] } },
            { value: s, itemStyle: { color: '#f5222d', borderRadius: [4, 4, 0, 0] } }
          ],
          label: {
            ...singleDimBarLabel,
            formatter: (param) =>
              dimPeople ? formatCountLabel(param.value) : formatWanLabel(param.value)
          }
        }
      ]
    }, { notMerge: true })
    return
  }

  const xData = subjectChartData.value.map(item => item.orgName)
  const primaryData = subjectChartData.value.map(item => item.primaryAmount)
  const juniorData = subjectChartData.value.map(item => item.juniorAmount)
  const seniorData = subjectChartData.value.map(item => item.seniorAmount)
  const dimPeopleSub = subjectChartDimension.value === CHART_DIM_PEOPLE

  subjectChartInstance.setOption({
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      backgroundColor: 'rgba(255, 255, 255, 0.95)',
      borderColor: '#e8e8e8',
      borderWidth: 1,
      textStyle: { color: '#333' },
      extraCssText: 'text-align: left;',
      formatter: function(params) {
        let total = 0
        params.forEach(item => { total += item.value })
        let result = params[0].name + '<br/>'
        params.forEach(item => {
          const v = dimPeopleSub ? formatCountLabel(item.value) : formatWanLabel(item.value)
          result += item.marker + item.seriesName + ': ' + v + (dimPeopleSub ? '人<br/>' : '元<br/>')
        })
        const totalStr = dimPeopleSub ? formatCountLabel(total) : formatWanLabel(total)
        result += '<hr style="margin: 3px 0">合计: ' + totalStr + (dimPeopleSub ? '人' : '元')
        return result
      }
    },
    legend: {
      data: subjectLegend,
      selected: { ...subjectLegendSelected.value },
      bottom: 5,
      itemWidth: 14,
      itemHeight: 14,
      textStyle: { fontSize: 12, color: '#666' },
      itemGap: 15
    },
    grid: {
      left: '3%',
      right: '4%',
      bottom: '12%',
      top: '12%',
      containLabel: true
    },
    xAxis: {
      type: 'category',
      data: xData,
      axisLabel: { fontSize: 12, color: '#666', rotate: xAxisCatRotate(), interval: 0 },
      axisLine: { lineStyle: { color: '#e8e8e8' } },
      axisTick: { show: false }
    },
    yAxis: {
      type: 'value',
      name: '',
      nameTextStyle: { color: '#999', fontSize: 11 },
      axisLabel: { fontSize: 11, color: '#999' },
      axisLine: { show: false },
      splitLine: { lineStyle: { color: '#f0f0f0', type: 'dashed' } }
    },
    series: [
      {
        name: '小学',
        type: 'bar',
        stack: 'total',
        barWidth: barWidthStack(),
        data: primaryData,
        itemStyle: { color: '#52c41a', borderRadius: [0, 0, 0, 0] },
        label: {
          show: true,
          position: 'top',
          rotate: barTopLabelRotate(),
          formatter: subjectLabelFormatter('primary'),
          fontSize: 12,
          fontWeight: 600,
          color: '#333'
        }
      },
      {
        name: '初中',
        type: 'bar',
        stack: 'total',
        barWidth: barWidthStack(),
        data: juniorData,
        itemStyle: { color: '#faad14', borderRadius: [0, 0, 0, 0] },
        label: {
          show: true,
          position: 'top',
          rotate: barTopLabelRotate(),
          formatter: subjectLabelFormatter('junior'),
          fontSize: 12,
          fontWeight: 600,
          color: '#333'
        }
      },
      {
        name: '高中',
        type: 'bar',
        stack: 'total',
        barWidth: barWidthStack(),
        data: seniorData,
        itemStyle: { color: '#f5222d', borderRadius: [4, 4, 0, 0] },
        label: {
          show: true,
          position: 'top',
          rotate: barTopLabelRotate(),
          formatter: subjectLabelFormatter('senior'),
          fontSize: 12,
          fontWeight: 600,
          color: '#333'
        }
      }
    ]
  }, { notMerge: true })
}

// 渲染课程类型统计图（全校区：堆叠；单校区：X 轴为班课/1对1）
const renderCourseTypeChart = () => {
  if (!courseTypeChart.value) return
  if (courseTypeChartData.value.length === 0) {
    courseTypeChartInstance?.clear()
    return
  }

  if (!courseTypeChartInstance) {
    courseTypeChartInstance = echarts.init(courseTypeChart.value)
    courseTypeChartInstance.on('legendselectchanged', onCourseTypeLegendSelectChanged)
  }

  if (!isAllOrg.value) {
    courseTypeLegendSelected.value = { '班课': true, '1对1': true }
    const row = getFirstChartRow(courseTypeChartData.value)
    const cls = (row.classAmount || 0)
    const oto = (row.oneToOneAmount || 0)
    const xDims = [...courseTypeLegend]
    const dimPeople = courseTypeChartDimension.value === CHART_DIM_PEOPLE

    courseTypeChartInstance.setOption({
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        borderColor: '#e8e8e8',
        borderWidth: 1,
        textStyle: { color: '#333' },
        extraCssText: 'text-align: left;',
        formatter: (params) => {
          const pt = params[0]
          return dimPeople
            ? `${pt.name}<br/>人数: ${formatCountLabel(pt.value)}人`
            : `${pt.name}<br/>金额: ${formatWanLabel(pt.value)}元`
        }
      },
      legend: { show: false },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '12%',
        top: '12%',
        containLabel: true
      },
      xAxis: {
        type: 'category',
        data: xDims,
        axisLabel: { fontSize: 12, color: '#666' },
        axisLine: { lineStyle: { color: '#e8e8e8' } },
        axisTick: { show: false }
      },
      yAxis: {
        type: 'value',
        name: '',
        nameTextStyle: { color: '#999', fontSize: 11 },
        axisLabel: { fontSize: 11, color: '#999' },
        axisLine: { show: false },
        splitLine: { lineStyle: { color: '#f0f0f0', type: 'dashed' } }
      },
      series: [
        {
          type: 'bar',
          name: '分类',
          barWidth: barWidthSingleCol(),
          data: [
            { value: cls, itemStyle: { color: '#3478f6', borderRadius: [4, 4, 0, 0] } },
            { value: oto, itemStyle: { color: '#ff9f43', borderRadius: [4, 4, 0, 0] } }
          ],
          label: {
            ...singleDimBarLabel,
            formatter: (param) =>
              dimPeople ? formatCountLabel(param.value) : formatWanLabel(param.value)
          }
        }
      ]
    }, { notMerge: true })
    return
  }

  const xData = courseTypeChartData.value.map(item => item.orgName)
  const classData = courseTypeChartData.value.map(item => item.classAmount)
  const oneToOneData = courseTypeChartData.value.map(item => item.oneToOneAmount)
  const dimPeopleCt = courseTypeChartDimension.value === CHART_DIM_PEOPLE

  courseTypeChartInstance.setOption({
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      backgroundColor: 'rgba(255, 255, 255, 0.95)',
      borderColor: '#e8e8e8',
      borderWidth: 1,
      textStyle: { color: '#333' },
      extraCssText: 'text-align: left;',
      formatter: function(params) {
        let total = 0
        params.forEach(item => { total += item.value })
        let result = params[0].name + '<br/>'
        params.forEach(item => {
          const v = dimPeopleCt ? formatCountLabel(item.value) : formatWanLabel(item.value)
          result += item.marker + item.seriesName + ': ' + v + (dimPeopleCt ? '人<br/>' : '元<br/>')
        })
        const totalStr = dimPeopleCt ? formatCountLabel(total) : formatWanLabel(total)
        result += '<hr style="margin: 3px 0">合计: ' + totalStr + (dimPeopleCt ? '人' : '元')
        return result
      }
    },
    legend: {
      data: courseTypeLegend,
      selected: { ...courseTypeLegendSelected.value },
      bottom: 5,
      itemWidth: 14,
      itemHeight: 14,
      textStyle: { fontSize: 12, color: '#666' },
      itemGap: 20
    },
    grid: {
      left: '3%',
      right: '4%',
      bottom: '12%',
      top: '12%',
      containLabel: true
    },
    xAxis: {
      type: 'category',
      data: xData,
      axisLabel: { fontSize: 12, color: '#666', rotate: xAxisCatRotate(), interval: 0 },
      axisLine: { lineStyle: { color: '#e8e8e8' } },
      axisTick: { show: false }
    },
    yAxis: {
      type: 'value',
      name: '',
      nameTextStyle: { color: '#999', fontSize: 11 },
      axisLabel: { fontSize: 11, color: '#999' },
      axisLine: { show: false },
      splitLine: { lineStyle: { color: '#f0f0f0', type: 'dashed' } }
    },
    series: [
      {
        name: '班课',
        type: 'bar',
        stack: 'total',
        barWidth: barWidthStack(),
        data: classData,
        itemStyle: { color: '#3478f6', borderRadius: [0, 0, 0, 0] },
        label: {
          show: true,
          position: 'top',
          rotate: barTopLabelRotate(),
          formatter: courseTypeLabelFormatter(false),
          fontSize: 12,
          fontWeight: 600,
          color: '#333'
        }
      },
      {
        name: '1对1',
        type: 'bar',
        stack: 'total',
        barWidth: barWidthStack(),
        data: oneToOneData,
        itemStyle: { color: '#ff9f43', borderRadius: [4, 4, 0, 0] },
        label: {
          show: true,
          position: 'top',
          rotate: barTopLabelRotate(),
          formatter: courseTypeLabelFormatter(true),
          fontSize: 12,
          fontWeight: 600,
          color: '#333'
        }
      }
    ]
  }, { notMerge: true })
}

// 渲染季节统计图（全校区：堆叠；单校区：X 轴为春夏秋冬四季）
const renderSeasonChart = () => {
  if (!seasonChart.value) return
  if (seasonChartData.value.length === 0) {
    seasonChartInstance?.clear()
    return
  }

  if (!seasonChartInstance) {
    seasonChartInstance = echarts.init(seasonChart.value)
    seasonChartInstance.on('legendselectchanged', onSeasonLegendSelectChanged)
  }

  if (!isAllOrg.value) {
    seasonLegendSelected.value = { '春季': true, '暑期': true, '秋季': true, '寒假': true }
    const row = getFirstChartRow(seasonChartData.value)

    const sp = (row.springAmount || 0)
    const su = (row.summerAmount || 0)
    const au = (row.autumnAmount || 0)
    const wi = (row.winterAmount || 0)

    const colors = ['#91CC75', '#FAC858', '#5470C6', '#EE6666']
    const dimPeople = seasonChartDimension.value === CHART_DIM_PEOPLE

    seasonChartInstance.setOption({
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        borderColor: '#e8e8e8',
        borderWidth: 1,
        textStyle: { color: '#333' },
        extraCssText: 'text-align: left;',
        formatter: (params) => {
          const pt = params[0]
          return dimPeople
            ? `${pt.name}<br/>人数: ${formatCountLabel(pt.value)}人`
            : `${pt.name}<br/>金额: ${formatWanLabel(pt.value)}元`
        }
      },
      legend: { show: false },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '12%',
        top: '12%',
        containLabel: true
      },
      xAxis: {
        type: 'category',
        data: [...seasonLegend],
        axisLabel: { fontSize: 12, color: '#666' },
        axisLine: { lineStyle: { color: '#e8e8e8' } },
        axisTick: { show: false }
      },
      yAxis: {
        type: 'value',
        name: '',
        nameTextStyle: { color: '#999', fontSize: 11 },
        axisLabel: { fontSize: 11, color: '#999' },
        axisLine: { show: false },
        splitLine: { lineStyle: { color: '#f0f0f0', type: 'dashed' } }
      },
      series: [
        {
          type: 'bar',
          name: '季节',
          barWidth: barWidthSeasonSingle(),
          barCategoryGap: '20px',
          data: [sp, su, au, wi].map((v, i) => ({
            value: v,
            itemStyle: { color: colors[i], borderRadius: [4, 4, 0, 0] }
          })),
          label: {
            ...singleDimBarLabel,
            formatter: (param) =>
              dimPeople ? formatCountLabel(param.value) : formatWanLabel(param.value)
          }
        }
      ]
    }, { notMerge: true })
    return
  }

  const xData = seasonChartData.value.map(item => item.orgName)
  const springData = seasonChartData.value.map(item => item.springAmount)
  const summerData = seasonChartData.value.map(item => item.summerAmount)
  const autumnData = seasonChartData.value.map(item => item.autumnAmount)
  const winterData = seasonChartData.value.map(item => item.winterAmount)
  const dimPeopleSe = seasonChartDimension.value === CHART_DIM_PEOPLE

  seasonChartInstance.setOption({
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      backgroundColor: 'rgba(255, 255, 255, 0.95)',
      borderColor: '#e8e8e8',
      borderWidth: 1,
      textStyle: { color: '#333' },
      extraCssText: 'text-align: left;',
      formatter: function(params) {
        let total = 0
        params.forEach(item => { total += item.value })
        let result = params[0].name + '<br/>'
        params.forEach(item => {
          const v = dimPeopleSe ? formatCountLabel(item.value) : formatWanLabel(item.value)
          result += item.marker + item.seriesName + ': ' + v + (dimPeopleSe ? '人<br/>' : '元<br/>')
        })
        const totalStr = dimPeopleSe ? formatCountLabel(total) : formatWanLabel(total)
        result += '<hr style="margin: 3px 0">合计: ' + totalStr + (dimPeopleSe ? '人' : '元')
        return result
      }
    },
    legend: {
      data: seasonLegend,
      selected: { ...seasonLegendSelected.value },
      bottom: 5,
      itemWidth: 14,
      itemHeight: 14,
      textStyle: { fontSize: 12, color: '#666' },
      itemGap: 10
    },
    grid: {
      left: '3%',
      right: '4%',
      bottom: '12%',
      top: '12%',
      containLabel: true
    },
    xAxis: {
      type: 'category',
      data: xData,
      axisLabel: { fontSize: 12, color: '#666', rotate: xAxisCatRotate(), interval: 0 },
      axisLine: { lineStyle: { color: '#e8e8e8' } },
      axisTick: { show: false }
    },
    yAxis: {
      type: 'value',
      name: '',
      nameTextStyle: { color: '#999', fontSize: 11 },
      axisLabel: { fontSize: 11, color: '#999' },
      axisLine: { show: false },
      splitLine: { lineStyle: { color: '#f0f0f0', type: 'dashed' } }
    },
    series: [
      {
        name: '春季',
        type: 'bar',
        stack: 'total',
        barWidth: barWidthStack(),
        data: springData,
        itemStyle: { color: '#91CC75', borderRadius: [0, 0, 0, 0] },
        label: {
          show: true,
          position: 'top',
          rotate: barTopLabelRotate(),
          formatter: seasonLabelFormatter('spring'),
          fontSize: 12,
          fontWeight: 600,
          color: '#333'
        }
      },
      {
        name: '暑期',
        type: 'bar',
        stack: 'total',
        barWidth: barWidthStack(),
        data: summerData,
        itemStyle: { color: '#FAC858', borderRadius: [0, 0, 0, 0] },
        label: {
          show: true,
          position: 'top',
          rotate: barTopLabelRotate(),
          formatter: seasonLabelFormatter('summer'),
          fontSize: 12,
          fontWeight: 600,
          color: '#333'
        }
      },
      {
        name: '秋季',
        type: 'bar',
        stack: 'total',
        barWidth: barWidthStack(),
        data: autumnData,
        itemStyle: { color: '#5470C6', borderRadius: [0, 0, 0, 0] },
        label: {
          show: true,
          position: 'top',
          rotate: barTopLabelRotate(),
          formatter: seasonLabelFormatter('autumn'),
          fontSize: 12,
          fontWeight: 600,
          color: '#333'
        }
      },
      {
        name: '寒假',
        type: 'bar',
        stack: 'total',
        barWidth: barWidthStack(),
        data: winterData,
        itemStyle: { color: '#EE6666', borderRadius: [4, 4, 0, 0] },
        label: {
          show: true,
          position: 'top',
          rotate: barTopLabelRotate(),
          formatter: seasonLabelFormatter('winter'),
          fontSize: 12,
          fontWeight: 600,
          color: '#333'
        }
      }
    ]
  }, { notMerge: true })
}

// 渲染报名分类统计图（全校区：堆叠；单校区：X 轴为新签/扩科/续费）
const renderSignUpCategoryChart = () => {
  if (!signUpCategoryChart.value) return
  if (signUpCategoryChartData.value.length === 0) {
    signUpCategoryChartInstance?.clear()
    return
  }

  if (!signUpCategoryChartInstance) {
    signUpCategoryChartInstance = echarts.init(signUpCategoryChart.value)
    signUpCategoryChartInstance.on('legendselectchanged', onSignUpCategoryLegendSelectChanged)
  }

  if (!isAllOrg.value) {
    signUpCategoryLegendSelected.value = { '新签': true, '扩科': true, '续费': true }
    const row = getFirstChartRow(signUpCategoryChartData.value)
    const ns = row.newSignCount || 0
    const ex = row.expandCount || 0
    const rn = row.renewCount || 0
    const dimPeople = signUpCategoryChartDimension.value === CHART_DIM_PEOPLE

    signUpCategoryChartInstance.setOption({
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        borderColor: '#e8e8e8',
        borderWidth: 1,
        textStyle: { color: '#333' },
        extraCssText: 'text-align: left;',
        formatter: (params) => {
          const pt = params[0]
          return dimPeople
            ? `${pt.name}<br/>人数: ${formatCountLabel(pt.value)}人`
            : `${pt.name}<br/>金额: ${formatMoney(pt.value)}元`
        }
      },
      legend: { show: false },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '12%',
        top: '12%',
        containLabel: true
      },
      xAxis: {
        type: 'category',
        data: [...signUpCategoryLegend],
        axisLabel: { fontSize: 12, color: '#666' },
        axisLine: { lineStyle: { color: '#e8e8e8' } },
        axisTick: { show: false }
      },
      yAxis: {
        type: 'value',
        name: '',
        nameTextStyle: { color: '#999', fontSize: 11 },
        axisLabel: { fontSize: 11, color: '#999' },
        axisLine: { show: false },
        splitLine: { lineStyle: { color: '#f0f0f0', type: 'dashed' } }
      },
      series: [
        {
          type: 'bar',
          name: '报名',
          barWidth: barWidthSingleCol(),
          data: [
            { value: ns, itemStyle: { color: '#3478f6', borderRadius: [4, 4, 0, 0] } },
            { value: ex, itemStyle: { color: '#52c41a', borderRadius: [4, 4, 0, 0] } },
            { value: rn, itemStyle: { color: '#faad14', borderRadius: [4, 4, 0, 0] } }
          ],
          label: {
            ...singleDimBarLabel,
            formatter: (param) =>
              dimPeople ? formatCountLabel(param.value) : formatMoney(param.value)
          }
        }
      ]
    }, { notMerge: true })
    return
  }

  const xData = signUpCategoryChartData.value.map(item => item.orgName)
  const newSignData = signUpCategoryChartData.value.map(item => item.newSignCount)
  const expandData = signUpCategoryChartData.value.map(item => item.expandCount)
  const renewData = signUpCategoryChartData.value.map(item => item.renewCount)
  const dimPeopleSu = signUpCategoryChartDimension.value === CHART_DIM_PEOPLE

  signUpCategoryChartInstance.setOption({
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      backgroundColor: 'rgba(255, 255, 255, 0.95)',
      borderColor: '#e8e8e8',
      borderWidth: 1,
      textStyle: { color: '#333' },
      extraCssText: 'text-align: left;',
      formatter: function(params) {
        let total = 0
        params.forEach(item => { total += item.value })
        let result = params[0].name + '<br/>'
        params.forEach(item => {
          const v = dimPeopleSu ? formatCountLabel(item.value) : formatMoney(item.value)
          result += item.marker + item.seriesName + ': ' + v + (dimPeopleSu ? '人<br/>' : '元<br/>')
        })
        const totalStr = dimPeopleSu ? formatCountLabel(total) : formatMoney(total)
        result += '<hr style="margin: 3px 0">合计: ' + totalStr + (dimPeopleSu ? '人' : '元')
        return result
      }
    },
    legend: {
      data: signUpCategoryLegend,
      selected: { ...signUpCategoryLegendSelected.value },
      bottom: 5,
      itemWidth: 14,
      itemHeight: 14,
      textStyle: { fontSize: 12, color: '#666' },
      itemGap: 20
    },
    grid: {
      left: '3%',
      right: '4%',
      bottom: '12%',
      top: '12%',
      containLabel: true
    },
    xAxis: {
      type: 'category',
      data: xData,
      axisLabel: { fontSize: 12, color: '#666', rotate: xAxisCatRotate(), interval: 0 },
      axisLine: { lineStyle: { color: '#e8e8e8' } },
      axisTick: { show: false }
    },
    yAxis: {
      type: 'value',
      name: '',
      nameTextStyle: { color: '#999', fontSize: 11 },
      axisLabel: { fontSize: 11, color: '#999' },
      axisLine: { show: false },
      splitLine: { lineStyle: { color: '#f0f0f0', type: 'dashed' } }
    },
    series: [
      {
        name: '新签',
        type: 'bar',
        stack: 'total',
        barWidth: barWidthStack(),
        data: newSignData,
        itemStyle: { color: '#3478f6', borderRadius: [0, 0, 0, 0] },
        label: {
          show: true,
          position: 'top',
          rotate: barTopLabelRotate(),
          formatter: signUpCategoryLabelFormatter('newSign'),
          fontSize: 12,
          fontWeight: 600,
          color: '#333'
        }
      },
      {
        name: '扩科',
        type: 'bar',
        stack: 'total',
        barWidth: barWidthStack(),
        data: expandData,
        itemStyle: { color: '#52c41a', borderRadius: [0, 0, 0, 0] },
        label: {
          show: true,
          position: 'top',
          rotate: barTopLabelRotate(),
          formatter: signUpCategoryLabelFormatter('expand'),
          fontSize: 12,
          fontWeight: 600,
          color: '#333'
        }
      },
      {
        name: '续费',
        type: 'bar',
        stack: 'total',
        barWidth: barWidthStack(),
        data: renewData,
        itemStyle: { color: '#faad14', borderRadius: [4, 4, 0, 0] },
        label: {
          show: true,
          position: 'top',
          rotate: barTopLabelRotate(),
          formatter: signUpCategoryLabelFormatter('renew'),
          fontSize: 12,
          fontWeight: 600,
          color: '#333'
        }
      }
    ]
  }, { notMerge: true })
}

/** 实时班课在班统计 tooltip 内「查看详情」：整行可点、便于移入浮层后点击 */
const buildClassStudentDetailLinkHtml = (orgId, orgName, startDate, endDate) => {
  const path = `/classStudentStatistics?creatOrg=${orgId}&orgName=${encodeURIComponent(orgName)}&startDate=${startDate}&endDate=${endDate}`
  const safePath = String(path).replace(/\\/g, '\\\\').replace(/'/g, "\\'")
  return `<div style="margin-top:10px;padding-top:10px;border-top:1px solid #eee"><a href="javascript:void(0)" onclick="event.stopPropagation();event.preventDefault();window.__vueRouterPush&&window.__vueRouterPush('${safePath}')" style="display:block;width:100%;box-sizing:border-box;color:#2466f6;font-size:14px;font-weight:500;text-decoration:none;pointer-events:auto;background:#e8f1ff;padding:12px 14px;border-radius:8px;cursor:pointer;text-align:center;line-height:1.35;user-select:none;-webkit-tap-highlight-color:rgba(36,102,246,0.12);">查看详情</a></div>`
}

const classStudentChartTooltipCommon = {
  enterable: true,
  hideDelay: 450,
  confine: true,
  triggerOn: 'mousemove|click'
}

// 渲染实时班课在班人数统计图（全校区：堆叠；单校区：X 轴为小学/初中/高中）
const renderClassStudentChart = () => {
  if (!classStudentChart.value) return
  if (classStudentChartData.value.length === 0) {
    classStudentChartInstance?.clear()
    return
  }

  if (!classStudentChartInstance) {
    classStudentChartInstance = echarts.init(classStudentChart.value)
    classStudentChartInstance.on('legendselectchanged', onClassStudentLegendSelectChanged)
  }

  if (!isAllOrg.value) {
    classStudentLegendSelected.value = { '小学': true, '初中': true, '高中': true }
    const row = getFirstChartRow(classStudentChartData.value)
    const p = row.primaryCount || 0
    const j = row.juniorCount || 0
    const s = row.seniorCount || 0
    const startDate = currentDateRange.value[0]
    const endDate = currentDateRange.value[1]
    const dimCs = classStudentChartDimension.value
    const isClassCs = dimCs === CHART_DIM_CLASS

    classStudentChartInstance.setOption({
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        ...classStudentChartTooltipCommon,
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        borderColor: '#e8e8e8',
        borderWidth: 1,
        textStyle: { color: '#333' },
        extraCssText: 'text-align:left;pointer-events:auto;',
        formatter: (params) => {
          const pt = params[0]
          const orgId = row.orgId
          const orgName = row.orgName
          const line = isClassCs
            ? `${pt.name}<br/>班级: ${formatCountLabel(pt.value)}个`
            : `${pt.name}<br/>人数: ${formatCountLabel(pt.value)}人`
          return `${line}${buildClassStudentDetailLinkHtml(orgId, orgName, startDate, endDate)}`
        }
      },
      legend: { show: false },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '12%',
        top: '12%',
        containLabel: true
      },
      xAxis: {
        type: 'category',
        data: [...classStudentLegend],
        axisLabel: { fontSize: 12, color: '#666' },
        axisLine: { lineStyle: { color: '#e8e8e8' } },
        axisTick: { show: false }
      },
      yAxis: {
        type: 'value',
        name: '',
        nameTextStyle: { color: '#999', fontSize: 11 },
        axisLabel: { fontSize: 11, color: '#999' },
        axisLine: { show: false },
        splitLine: { lineStyle: { color: '#f0f0f0', type: 'dashed' } }
      },
      series: [
        {
          type: 'bar',
          name: '在班人数',
          barWidth: barWidthSingleCol(),
          data: [
            { value: p, itemStyle: { color: '#52c41a', borderRadius: [4, 4, 0, 0] } },
            { value: j, itemStyle: { color: '#faad14', borderRadius: [4, 4, 0, 0] } },
            { value: s, itemStyle: { color: '#f5222d', borderRadius: [4, 4, 0, 0] } }
          ],
          label: {
            ...singleDimBarLabel,
            formatter: (params) => formatCountLabel(params.value)
          }
        }
      ]
    }, { notMerge: true })
    return
  }

  const xData = classStudentChartData.value.map(item => item.orgName)
  const primaryData = classStudentChartData.value.map(item => item.primaryCount)
  const juniorData = classStudentChartData.value.map(item => item.juniorCount)
  const seniorData = classStudentChartData.value.map(item => item.seniorCount)

  const startDate = currentDateRange.value[0]
  const endDate = currentDateRange.value[1]
  const isClassCsAll = classStudentChartDimension.value === CHART_DIM_CLASS

  classStudentChartInstance.setOption({
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      ...classStudentChartTooltipCommon,
      backgroundColor: 'rgba(255, 255, 255, 0.95)',
      borderColor: '#e8e8e8',
      borderWidth: 1,
      textStyle: { color: '#333' },
      extraCssText: 'text-align:left;pointer-events:auto;',
      formatter: function(params) {
        const orgId = classStudentChartData.value[params[0].dataIndex]?.orgId || ''
        const orgName = classStudentChartData.value[params[0].dataIndex]?.orgName || ''
        let total = 0
        params.forEach(item => { total += item.value })
        let result = params[0].name + '<br/>'
        params.forEach(item => {
          const v = formatCountLabel(item.value)
          const unit = isClassCsAll ? '个班<br/>' : '人<br/>'
          result += item.marker + item.seriesName + ': ' + v + unit
        })
        const totalStr = formatCountLabel(total)
        const totalUnit = isClassCsAll ? '个班' : '人'
        result += `<hr style="margin: 3px 0">合计: ${totalStr}${totalUnit}`
        result += buildClassStudentDetailLinkHtml(orgId, orgName, startDate, endDate)
        return result
      }
    },
    legend: {
      data: classStudentLegend,
      selected: { ...classStudentLegendSelected.value },
      bottom: 5,
      itemWidth: 14,
      itemHeight: 14,
      textStyle: { fontSize: 12, color: '#666' },
      itemGap: 20
    },
    grid: {
      left: '3%',
      right: '4%',
      bottom: '12%',
      top: '12%',
      containLabel: true
    },
    xAxis: {
      type: 'category',
      data: xData,
      axisLabel: { fontSize: 12, color: '#666', rotate: xAxisCatRotate(), interval: 0 },
      axisLine: { lineStyle: { color: '#e8e8e8' } },
      axisTick: { show: false }
    },
    yAxis: {
      type: 'value',
      name: '',
      nameTextStyle: { color: '#999', fontSize: 11 },
      axisLabel: { fontSize: 11, color: '#999' },
      axisLine: { show: false },
      splitLine: { lineStyle: { color: '#f0f0f0', type: 'dashed' } }
    },
    series: [
      {
        name: '小学',
        type: 'bar',
        stack: 'total',
        barWidth: barWidthStack(),
        data: primaryData,
        itemStyle: { color: '#52c41a', borderRadius: [0, 0, 0, 0] },
        label: {
          show: true,
          position: 'top',
          rotate: barTopLabelRotate(),
          formatter: classStudentLabelFormatter('primary'),
          fontSize: 12,
          fontWeight: 600,
          color: '#333'
        }
      },
      {
        name: '初中',
        type: 'bar',
        stack: 'total',
        barWidth: barWidthStack(),
        data: juniorData,
        itemStyle: { color: '#faad14', borderRadius: [0, 0, 0, 0] },
        label: {
          show: true,
          position: 'top',
          rotate: barTopLabelRotate(),
          formatter: classStudentLabelFormatter('junior'),
          fontSize: 12,
          fontWeight: 600,
          color: '#333'
        }
      },
      {
        name: '高中',
        type: 'bar',
        stack: 'total',
        barWidth: barWidthStack(),
        data: seniorData,
        itemStyle: { color: '#f5222d', borderRadius: [4, 4, 0, 0] },
        label: {
          show: true,
          position: 'top',
          rotate: barTopLabelRotate(),
          formatter: classStudentLabelFormatter('senior'),
          fontSize: 12,
          fontWeight: 600,
          color: '#333'
        }
      }
    ]
  }, { notMerge: true })
}

// 报课分校区统计：应收金额柱状图 + 应退金额/核销金额堆叠图（全校区）或三根独立柱（单校区）
const renderEnrollOrgChart = () => {
  if (!enrollOrgChart.value) return
  if (enrollOrgChartData.value.length === 0) {
    enrollOrgChartInstance?.clear()
    return
  }

  if (!enrollOrgChartInstance) {
    enrollOrgChartInstance = echarts.init(enrollOrgChart.value)
  }

  const xData = enrollOrgChartData.value.map(item => item.orgName)
  const enrollData = enrollOrgChartData.value.map(item => item.enrollPayableAmount)
  const refundRaw = enrollOrgChartData.value.map(item => item.refundPayableAmount)
  const consumeRaw = enrollOrgChartData.value.map(item => item.lessonConsumeAmount)
  const selected = enrollOrgLegendSelected.value
  const refundData = refundRaw.map(v => selected['退课应退'] !== false ? v : 0)
  const consumeData = consumeRaw.map(v => selected['学费核销'] !== false ? v : 0)

  // 单校区模式：显示三根独立柱
  if (!isAllOrg.value) {
    enrollOrgLegendSelected.value = { '报名应收': true, '退课应退': true, '学费核销': true }
    const row = getFirstChartRow(enrollOrgChartData.value)
    const enroll = row.enrollPayableAmount || 0
    const refund = row.refundPayableAmount || 0
    const consume = row.lessonConsumeAmount || 0
    const xDims = ['报名应收', '退课应退', '学费核销']

    enrollOrgChartInstance.setOption({
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        borderColor: '#e8e8e8',
        borderWidth: 1,
        textStyle: { color: '#333' },
        extraCssText: 'text-align: left;',
        formatter: (params) => {
          const p = params[0]
          return `${p.name}<br/>金额: ${formatWanLabel(p.value)}元`
        }
      },
      legend: { show: false },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '12%',
        top: '12%',
        containLabel: true
      },
      xAxis: {
        type: 'category',
        data: xDims,
        axisLabel: { fontSize: 12, color: '#666' },
        axisLine: { lineStyle: { color: '#e8e8e8' } },
        axisTick: { show: false }
      },
      yAxis: {
        type: 'value',
        name: '',
        nameTextStyle: { color: '#999', fontSize: 11 },
        axisLabel: { fontSize: 11, color: '#999' },
        axisLine: { show: false },
        splitLine: { lineStyle: { color: '#f0f0f0', type: 'dashed' } }
      },
      series: [
        {
          type: 'bar',
          name: '金额',
          barWidth: barWidthSingleCol(),
          data: [
            {
              value: enroll,
              itemStyle: {
                color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                  { offset: 0, color: '#36b371' },
                  { offset: 1, color: '#22B894' }
                ]),
                borderRadius: [4, 4, 0, 0]
              }
            },
            {
              value: refund,
              itemStyle: {
                color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                  { offset: 0, color: '#ff7875' },
                  { offset: 1, color: '#EE6666' }
                ]),
                borderRadius: [4, 4, 0, 0]
              }
            },
            {
              value: consume,
              itemStyle: {
                color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                  { offset: 0, color: '#faad14' },
                  { offset: 1, color: '#d48806' }
                ]),
                borderRadius: [4, 4, 0, 0]
              }
            }
          ],
          label: {
            ...singleDimBarLabel,
            formatter: (p) => formatWanLabel(p.value)
          }
        }
      ]
    }, { notMerge: true })
    return
  }

  // 全校区模式：应收金额柱状图 + 应退金额/核销金额堆叠图
  enrollOrgChartInstance.setOption({
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      backgroundColor: 'rgba(255, 255, 255, 0.95)',
      borderColor: '#e8e8e8',
      borderWidth: 1,
      textStyle: { color: '#333' },
      extraCssText: 'text-align: left;',
      formatter: function(params) {
        let result = params[0].name + '<br/>'
        let totalStack = 0
        params.forEach(item => {
          const v = formatWanLabel(item.value)
          result += item.marker + item.seriesName + ': ' + v + '元<br/>'
          if (item.seriesName === '退课应退' || item.seriesName === '学费核销') {
            totalStack += item.value
          }
        })
        const enrollVal = enrollData[params[0].dataIndex] || 0
        result += '<hr style="margin: 3px 0">报名应收: ' + formatWanLabel(enrollVal) + '元'
        if (totalStack > 0) {
          result += '<br/>应退+核销: ' + formatWanLabel(totalStack) + '元'
        }
        return result
      }
    },
    legend: {
      data: enrollOrgLegend,
      bottom: 5,
      itemWidth: 14,
      itemHeight: 14,
      textStyle: { fontSize: 12, color: '#666' },
      itemGap: 16,
      selected: enrollOrgLegendSelected.value
    },
    grid: {
      left: '3%',
      right: '4%',
      bottom: '18%',
      top: '12%',
      containLabel: true
    },
    xAxis: {
      type: 'category',
      data: xData,
      axisLabel: {
        fontSize: 12,
        color: '#666',
        rotate: xAxisCatRotate(),
        interval: 0
      },
      axisLine: { lineStyle: { color: '#e8e8e8' } },
      axisTick: { show: false }
    },
    yAxis: {
      type: 'value',
      name: '',
      nameTextStyle: { color: '#999', fontSize: 11 },
      axisLabel: { fontSize: 11, color: '#999' },
      axisLine: { show: false },
      splitLine: { lineStyle: { color: '#f0f0f0', type: 'dashed' } }
    },
    series: [
      {
        name: '报名应收',
        type: 'bar',
        barWidth: barWidthGrouped(),
        barGap: '20%',
        data: enrollData,
        itemStyle: {
          color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
            { offset: 0, color: '#36b371' },
            { offset: 1, color: '#22B894' }
          ]),
          borderRadius: [4, 4, 0, 0]
        },
        label: {
          show: true,
          position: 'top',
          rotate: 30,
          fontSize: 12,
          fontWeight: 600,
          color: '#333',
          formatter: enrollOrgLabelFormatter('enroll')
        }
      },
      {
        name: '退课应退',
        type: 'bar',
        stack: 'amount',
        barWidth: barWidthGrouped(),
        data: refundData,
        itemStyle: {
          color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
            { offset: 0, color: '#ff7875' },
            { offset: 1, color: '#EE6666' }
          ]),
          borderRadius: [0, 0, 0, 0]
        },
        label: {
          show: true,
          position: 'top',
          rotate: 30,
          fontSize: 12,
          fontWeight: 600,
          color: '#333',
          formatter: enrollOrgLabelFormatter('refund')
        }
      },
      {
        name: '学费核销',
        type: 'bar',
        stack: 'amount',
        barWidth: barWidthGrouped(),
        data: consumeData,
        itemStyle: {
          color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
            { offset: 0, color: '#5a9cf8' },
            { offset: 1, color: '#3478f6' }
          ]),
          borderRadius: [4, 4, 0, 0]
        },
        label: {
          show: true,
          position: 'top',
          rotate: 30,
          fontSize: 12,
          fontWeight: 600,
          color: '#333',
          formatter: enrollOrgLabelFormatter('consume')
        }
      }
    ]
  }, { notMerge: true })
  // 只在图表首次初始化时绑定事件监听器
  if (!enrollOrgChartInstance._legendSelectBound) {
    enrollOrgChartInstance.on('legendselectchanged', onEnrollOrgLegendSelectChanged)
    enrollOrgChartInstance._legendSelectBound = true
  }
}

// 报课剩余课时统计：单根柱状图，支持切换Y轴维度（剩余金额/剩余课时）
const renderRemainingHoursChart = () => {
  if (!remainingHoursChart.value) return
  if (remainingHoursChartData.value.length === 0) {
    remainingHoursChartInstance?.clear()
    return
  }

  if (!remainingHoursChartInstance) {
    remainingHoursChartInstance = echarts.init(remainingHoursChart.value)
  }

  const isAmountDim = remainingHoursChartDimension.value === CHART_DIM_AMOUNT
  const xData = remainingHoursChartData.value.map(item => item.orgName)
  const chartData = remainingHoursChartData.value.map(item =>
    isAmountDim ? item.remainingAmount : item.remainingHours
  )

  // 单校区模式：显示单根柱
  if (!isAllOrg.value) {
    const row = getFirstChartRow(remainingHoursChartData.value)
    const value = isAmountDim ? (row.remainingAmount || 0) : (row.remainingHours || 0)
    const xDims = [isAmountDim ? '剩余金额' : '剩余课时']

    remainingHoursChartInstance.setOption({
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        borderColor: '#e8e8e8',
        borderWidth: 1,
        textStyle: { color: '#333' },
        extraCssText: 'text-align: left;',
        formatter: (params) => {
          const p = params[0]
          return isAmountDim
            ? `${p.name}<br/>金额: ${formatWanLabel(p.value)}元`
            : `${p.name}<br/>课时: ${formatCountLabel(p.value)}课时`
        }
      },
      legend: { show: false },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '12%',
        top: '12%',
        containLabel: true
      },
      xAxis: {
        type: 'category',
        data: xDims,
        axisLabel: { fontSize: 12, color: '#666' },
        axisLine: { lineStyle: { color: '#e8e8e8' } },
        axisTick: { show: false }
      },
      yAxis: {
        type: 'value',
        name: '',
        nameTextStyle: { color: '#999', fontSize: 11 },
        axisLabel: { fontSize: 11, color: '#999' },
        axisLine: { show: false },
        splitLine: { lineStyle: { color: '#f0f0f0', type: 'dashed' } }
      },
      series: [
        {
          type: 'bar',
          name: isAmountDim ? '剩余金额' : '剩余课时',
          barWidth: barWidthSingleCol(),
          barGap: '12px',
          barCategoryGap: '12px',
          data: [
            {
              value: value,
              itemStyle: {
                color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                  { offset: 0, color: '#36b371' },
                  { offset: 1, color: '#22B894' }
                ]),
                borderRadius: [4, 4, 0, 0]
              }
            }
          ],
          label: {
            ...singleDimBarLabel,
            formatter: (p) => isAmountDim ? formatWanLabel(p.value) : formatCountLabel(p.value)
          }
        }
      ]
    }, { notMerge: true })
    return
  }

  // 全校区模式：X轴为校区，显示单根柱状图
  remainingHoursChartInstance.setOption({
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      backgroundColor: 'rgba(255, 255, 255, 0.95)',
      borderColor: '#e8e8e8',
      borderWidth: 1,
      textStyle: { color: '#333' },
      extraCssText: 'text-align: left;',
      formatter: function(params) {
        const p = params[0]
        const row = remainingHoursChartData.value[p.dataIndex]
        if (isAmountDim) {
          return `${p.name}<br/>剩余金额: ${formatWanLabel(p.value)}元<br/>剩余课时: ${formatCountLabel(row?.remainingHours || 0)}课时`
        } else {
          return `${p.name}<br/>剩余课时: ${formatCountLabel(p.value)}课时<br/>剩余金额: ${formatWanLabel(row?.remainingAmount || 0)}元`
        }
      }
    },
    legend: { show: false },
    grid: {
      left: '3%',
      right: '4%',
      bottom: '18%',
      top: '12%',
      containLabel: true
    },
    xAxis: {
      type: 'category',
      data: xData,
      axisLabel: {
        fontSize: 12,
        color: '#666',
        rotate: xAxisCatRotate(),
        interval: 0
      },
      axisLine: { lineStyle: { color: '#e8e8e8' } },
      axisTick: { show: false }
    },
    yAxis: {
      type: 'value',
      name: isAmountDim ? '' : '课时',
      nameTextStyle: { color: '#999', fontSize: 11 },
      axisLabel: {
        fontSize: 11,
        color: '#999',
        formatter: (v) => isAmountDim ? formatWanLabel(v) : formatCountLabel(v)
      },
      axisLine: { show: false },
      splitLine: { lineStyle: { color: '#f0f0f0', type: 'dashed' } }
    },
    series: [
      {
        name: isAmountDim ? '剩余金额' : '剩余课时',
        type: 'bar',
        barWidth: barWidthGrouped(),
        data: chartData,
        barGap: '12px',
        barCategoryGap: '12px',
        itemStyle: {
          color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
            { offset: 0, color: '#36b371' },
            { offset: 1, color: '#22B894' }
          ]),
          borderRadius: [4, 4, 0, 0]
        },
        label: {
          show: true,
          position: 'top',
          rotate: barTopLabelRotate(),
          fontSize: 12,
          fontWeight: 600,
          color: '#333',
          formatter: (p) => isAmountDim ? formatWanLabel(p.value) : formatCountLabel(p.value)
        }
      }
    ]
  }, { notMerge: true })
}

// 渲染所有图表
const renderCharts = () => {
  renderBranchChart()
  renderSubjectChart()
  renderCourseTypeChart()
  renderSeasonChart()
  renderSignUpCategoryChart()
  renderClassStudentChart()
  renderEnrollOrgChart()
  renderRemainingHoursChart()
}

// 窗口大小变化时重新渲染图表
// 统一加载数据
const loadData = async () => {
  await loadRevenueCards()
  await loadBranchRevenueCharts()
}

const loading = ref(false)
const refreshing = ref(false)

const handleRefresh = async () => {
  if (refreshing.value) return
  refreshing.value = true
  try {
    resetPageData()
    await loadData()
  } finally {
    refreshing.value = false
  }
}

// iframe消息处理：接收web端导出参数请求
const handleIframeMessage = (event) => {
  const { data } = event
  if (data && data.type === 'getExportParams') {
    // 获取当前选中的参数
    const orgId = getOrgId() // 使用现有的getOrgId方法
    const startDate = currentDateRange.value?.[0] || ''
    const endDate = currentDateRange.value?.[1] || ''

    // 向web端返回参数
    if (event.source && event.source.postMessage) {
      event.source.postMessage(
        {
          type: 'exportParams',
          orgId,
          startDate,
          endDate
        },
        '*'
      )
    }
  }
}

onMounted(async () => {
  await getUserInfo()
  if(route?.query?.token) {
    localStorage.setItem('token', route?.query?.token)
  }
  window.__vueRouterPush = (path) => router.push(path)
  if(route?.query?.selctedOrgId) {
    await loadOrgList()
    selectedOrg.value = route?.query?.selctedOrgId
    sessionStorage.setItem(STORAGE_KEY_ORG, selectedOrg.value)
  }
  window.__vueRouterPush = (path) => router.push(path)
  await loadOrgList()
  // 校区列表加载完成后再恢复缓存
  restoreOrgFromStorage()
  initDateRange()
  await loadData()
  document.addEventListener('click', onDocumentClickCloseOrgPc)
  window.addEventListener('resize', handleResize)
  // 添加iframe消息监听
  window.addEventListener('message', handleIframeMessage)
})

onBeforeUnmount(() => {
  pcAnchorScrollCleanup?.()
})

onUnmounted(() => {
  window.removeEventListener('resize', handleResize)
  document.removeEventListener('click', onDocumentClickCloseOrgPc)
  delete window.__vueRouterPush
  branchChartInstance?.dispose()
  subjectChartInstance?.dispose()
  courseTypeChartInstance?.dispose()
  seasonChartInstance?.dispose()
  signUpCategoryChartInstance?.dispose()
  classStudentChartInstance?.dispose()
  enrollOrgChartInstance?.dispose()
  remainingHoursChartInstance?.dispose()
  // 清理iframe消息监听
  window.removeEventListener('message', handleIframeMessage)
})
</script>

<style lang="scss" scoped>
.revenue-statistics-page {
  background: linear-gradient(180deg, #e8f4ff 0%, #f5f6fa 30%);
  min-height: 100vh;
  padding-bottom: 20px;
  height: 100vh;
  overflow-y: scroll;
}

.header-section-pc {
  padding: 16px 16px 12px;
  background: transparent;
  margin-bottom: 12px;

  .header-row-pc {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 24px;
  }

  .org-select-row-pc {
    display: flex;
    align-items: center;
    flex-shrink: 0;
  }

  .org-select-wrap {
    position: relative;
    width: 100%;
    max-width: 200px;
    min-width: 200px;
  }

  .org-select-trigger {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    min-height: 32px;
    padding: 0 14px;
    cursor: pointer;

    &:hover {
      border-color: #91b5d9;
      box-shadow: 0 1px 3px rgba(15, 37, 64, 0.06);
    }

    &.is-open {
      border-color: #22B894;
      box-shadow: 0 0 0 2px rgba(34, 184, 148, 0.12);
    }

    &:focus-visible {
      border-color: #22B894;
      box-shadow: 0 0 0 2px rgba(34, 184, 148, 0.12);
    }
  }

  .org-select-trigger-text {
    flex: 1;
    min-width: 0;
    font-size: 14px;
    font-weight: 400;
    line-height: 22px;
    color: rgba(0, 0, 0, 0.88);
    text-align: left;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .org-select-chevron {
    flex-shrink: 0;
    font-size: 15px;
    color: rgba(0, 0, 0, 0.28);
  }

  .org-select-dropdown {
    position: absolute;
    left: 0;
    right: 0;
    top: calc(100% + 8px);
    z-index: 200;
    background: #fff;
    border: 1px solid #e4eaf2;
    border-radius: 6px;
    box-shadow:
      0 4px 14px rgba(15, 37, 64, 0.1),
      0 0 1px rgba(15, 37, 64, 0.06);
    max-height: 300px;
    overflow-x: hidden;
    overflow-y: auto;
  }

  .org-select-list {
    margin: 0;
    padding: 6px 0;
    list-style: none;
  }

  .org-select-item {
    padding: 10px 14px;
    font-size: 14px;
    line-height: 22px;
    color: rgba(0, 0, 0, 0.88);
    text-align: left;
    cursor: pointer;
    transition: background 0.15s, color 0.15s;

    &:hover {
      background: #f5f8fc;
    }

    &.active {
      color: #22B894;
      font-weight: 500;
      background: rgba(34, 184, 148, 0.08);
    }
  }

  .pc-date-filter-bar-inline {
    display: flex;
    flex-wrap: wrap;
    gap: 12px 24px;
    flex: 1;
    justify-content: flex-end;

    .pc-date-quick-tabs {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      gap: 4px 16px;
    }

    .pc-date-tab {
      position: relative;
      padding: 4px 6px 12px;
      border: none;
      background: transparent;
      font-size: 14px;
      color: rgba(0, 0, 0, 0.72);
      cursor: pointer;
      line-height: 22px;
      transition: color 0.2s;

      &:hover {
        color: #22B894;
      }

      &.active {
        color: #22B894;
        font-weight: 600;

        &::after {
          content: '';
          position: absolute;
          left: 6px;
          right: 6px;
          bottom: 4px;
          height: 3px;
          background: #22B894;
          border-radius: 2px 2px 0 0;
        }
      }
    }

    .pc-date-range-input {
      display: flex;
      align-items: center;
      gap: 10px;
      min-width: 268px;
      max-width: 340px;
      height: 28px;
      padding: 0 14px;
      background: #fff;
      border: 1px solid #d9e2ec;
      border-radius: 4px;
      box-shadow: 0 1px 2px rgba(15, 37, 64, 0.04);
      cursor: pointer;
      transition: border-color 0.2s, box-shadow 0.2s;

      &:hover {
        border-color: #91b5d9;
      }

      &.is-custom {
        border-color: #22B894;
        box-shadow: 0 0 0 2px rgba(34, 184, 148, 0.12);
      }

      .pc-date-range-input__icon {
        flex-shrink: 0;
        font-size: 16px;
        color: rgba(0, 0, 0, 0.28);
      }

      .pc-date-range-input__inner {
        flex: 1;
        min-width: 0;
        display: flex;
        align-items: center;
        justify-content: flex-start;
        gap: 8px;
        font-size: 14px;
      }

      .pc-date-range-input__part {
        color: rgba(0, 0, 0, 0.88);

        &.is-ph {
          color: rgba(0, 0, 0, 0.32);
        }
      }

      .pc-date-range-input__sep {
        color: rgba(0, 0, 0, 0.45);
        flex-shrink: 0;
      }
    }

    .refresh-btn-pc {
      flex-shrink: 0;
      display: flex;
      align-items: center;
      gap: 4px;
      height: 28px;

      .is-spinning {
        animation: spin 0.8s linear infinite;
      }
    }
  }
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}


.header-section {
  background: linear-gradient(135deg, #22B894 0%, #36b371 100%);
  padding: 8px 16px 24px;
  border-radius: 0 0 24px 24px;
  box-shadow: 0 4px 16px rgba(34, 184, 148, 0.2);

  .h5-header-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
  }

  .h5-header-right {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .refresh-btn-h5 {
    background: rgba(255, 255, 255, 0.25);
    border: 1px solid rgba(255, 255, 255, 0.4);
    color: #fff;
    min-width: 36px;
    padding: 0 8px;

    &:active {
      background: rgba(255, 255, 255, 0.35);
    }

    .van-icon {
      font-size: 16px;

      &.is-spinning {
        animation: spin 0.8s linear infinite;
      }
    }
  }

  .org-select-card {
    display: flex;
    align-items: center;
  }

  .org-select-btn {
    color: #fff;
    font-size: 17px;
    font-weight: 600;
    padding: 0 12px;
    height: 40px;
    display: flex;
    align-items: center;
    cursor: pointer;
    background: transparent;
  }
}

.overview-section {
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(34, 184, 148, 0.08);
  margin: -22px 10px 12px 10px;
  padding: 12px;
  position: relative;
  z-index: 10;
}

.overview-header {
  display: flex;
  justify-content: space-between;
}

.section-title {
  font-size: 17px;
  font-weight: 600;
  color: #1a1a1a;
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;

  .title-icon {
    width: 4px;
    height: 16px;
    background: linear-gradient(180deg, #22B894 0%, #36b371 100%);
    border-radius: 2px;

    &.period-icon {
      background: linear-gradient(180deg, #3478f6 0%, #5a9fff 100%);
    }

    &.branch-icon {
      background: linear-gradient(180deg, #ff9f43 0%, #ffbe76 100%);
    }

    &.subject-icon {
      background: linear-gradient(180deg, #52c41a 0%, #95de64 100%);
    }

    &.course-icon {
      background: linear-gradient(180deg, #722ed1 0%, #b37feb 100%);
    }

    &.season-icon {
      background: linear-gradient(180deg, #13c2c2 0%, #36cfc9 100%);
    }

    &.signup-icon {
      background: linear-gradient(180deg, #fa8c16 0%, #fdba74 100%);
    }

    &.class-student-icon {
      background: linear-gradient(180deg, #f5222d 0%, #ff7875 100%);
    }

    &.enroll-org-icon {
      background: linear-gradient(180deg, #9254de 0%, #b37feb 100%);
    }

    &.remaining-hours-icon {
      background: linear-gradient(180deg, #eb7b16 0%, #ffc069 100%);
    }
  }

  .subtitle {
    font-size: 12px;
    font-weight: 400;
    color: #999;
  }
}

.month-btn {
  display: flex;
  align-items: center;
  gap: 4px;
  background: rgba(255, 255, 255, 0.95);
  border: 1px solid rgba(255, 255, 255, 0.5);
  color: #22B894;
  font-size: 13px;
  border-radius: 16px;
  padding: 4px 12px;
  height: 28px;

  &:active {
    background: rgba(255, 255, 255, 0.85);
  }
}

.overview-cards {
  display: flex;
  gap: 12px;
}

.overview-card {
  flex: 1;
  min-width: 0;
  background: linear-gradient(135deg, #f8faff 0%, #fff 100%);
  border-radius: 14px;
  padding: 16px 12px;
  text-align: center;
  border: 1px solid #e8eef5;
  transition: all 0.3s ease;

  &:active {
    transform: scale(0.98);
  }

  &.income {
    border-left: 3px solid #22B894;
  }

  &.refund {
    border-left: 3px solid #EE6666;
  }

  .card-content {
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  .card-label {
    font-size: 12px;
    color: #8c8c8c;
    margin-bottom: 4px;
    white-space: nowrap;
  }

  .card-value {
    font-size: 22px;
    font-weight: 700;
    background: linear-gradient(135deg, #22B894 0%, #36b371 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  &.refund .card-value {
    background: linear-gradient(135deg, #EE6666 0%, #ff7875 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
}

.stats-section {
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(52, 120, 246, 0.08);
  margin: 0 10px 12px 10px;
  padding: 12px;
}

.chart-dim-toggle {
  display: flex;
  gap: 8px;
  margin-bottom: 8px;
}

.chart-dim-toggle--three .toggle-btn {
  font-size: 11px;
  padding: 5px 4px;
}

.chart-dim-toggle .toggle-btn {
  flex: 1;
  padding: 5px 0;
  text-align: center;
  font-size: 12px;
  color: #999;
  background: #f7f8fa;
  border-radius: 8px;
  border: 1px solid transparent;
  transition: all 0.25s ease;
  max-width: 80px;

  &.active {
    color: #22b894;
    background: #e8faf4;
    border-color: #b7eb8f;
    font-weight: 600;
  }

  &:active {
    transform: scale(0.98);
  }
}

.stats-cards {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
}

.stat-card {
  background: linear-gradient(135deg, #f8faff 0%, #fff 100%);
  border-radius: 12px;
  padding: 16px 12px;
  text-align: center;
  border: 1px solid #e8eef5;

  .stat-value {
    font-size: 20px;
    font-weight: 700;
    background: linear-gradient(135deg, #3478f6 0%, #5a9fff 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    margin-bottom: 4px;

    &.income-color {
      background: linear-gradient(135deg, #22B894 0%, #36b371 100%);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
    }

    &.refund-color {
      background: linear-gradient(135deg, #EE6666 0%, #ff7875 100%);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
    }
  }

  .stat-label {
    font-size: 12px;
    color: #8c8c8c;
  }
}

.watermark-layer {
  position: fixed;
  inset: 0;
  pointer-events: none;
  z-index: 9999;
  overflow: hidden;
}

.watermark-text {
  position: absolute;
  transform-origin: 0 0;
  transform: rotate(-24deg);
  font-size: 14px;
  color: rgba(0, 0, 0, 0.12);
  white-space: nowrap;
  user-select: none;
}

.chart-section {
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(52, 120, 246, 0.08);
  margin: 0 10px 12px 10px;
  padding: 12px;
}

.bar-chart {
  min-width: 100%;
  height: 240px;
}

.bar-chart-wrapper {
  position: relative;

  .van-empty {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: #fff;
  }
}

.chart-legend {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 12px;

  .legend-item {
    display: flex;
    align-items: center;
    font-size: 12px;
    color: #666;

    .legend-dot {
      width: 10px;
      height: 10px;
      border-radius: 50%;
      margin-right: 4px;
    }
  }
}
</style>
